export interface Question {
  questionNumber: number;
  question: string;
  answerOptions: {
    text: string;
    rationale: string;
    isCorrect: boolean;
  }[];
  hint: string;
}

export const practiceTestQuestions: Question[] = [
  {
    "questionNumber": 1,
    "question": "Which of the following is considered a fixture in a real estate transaction?",
    "answerOptions": [
      {
        "text": "A freestanding refrigerator",
        "rationale": "A freestanding refrigerator is typically considered personal property as it is not permanently attached to the property.",
        "isCorrect": false
      },
      {
        "text": "A custom-built bookshelf that is attached to the wall",
        "rationale": "Fixtures are items of personal property that are attached to the land or building in such a way that they are considered part of the real property. A custom-built, attached bookshelf fits this description.",
        "isCorrect": true
      },
      {
        "text": "A decorative rug",
        "rationale": "A decorative rug is personal property and can be easily removed without damaging the property.",
        "isCorrect": false
      },
      {
        "text": "A potted plant on the patio",
        "rationale": "A potted plant is personal property and is not considered a fixture.",
        "isCorrect": false
      }
    ],
    "hint": "Think about whether the item is permanently attached to the property."
  },
  {
    "questionNumber": 2,
    "question": "What is the primary purpose of the Real Estate Settlement Procedures Act (RESPA)?",
    "answerOptions": [
      {
        "text": "To set the commission rates for real estate agents.",
        "rationale": "RESPA does not regulate commission rates.",
        "isCorrect": false
      },
      {
        "text": "To provide consumer protection for loans on residential properties.",
        "rationale": "RESPA was enacted to protect consumers by requiring lenders to provide disclosures about closing costs and to prohibit kickbacks and referral fees.",
        "isCorrect": true
      },
      {
        "text": "To establish zoning laws for residential and commercial properties.",
        "rationale": "Zoning laws are established at the local level and are not the subject of RESPA.",
        "isCorrect": false
      },
      {
        "text": "To regulate the activities of real estate appraisers.",
        "rationale": "While RESPA deals with settlement services, the specific regulation of appraisers falls under other laws and regulations.",
        "isCorrect": false
      }
    ],
    "hint": "This act is all about making the home buying process more transparent for the consumer."
  },
  {
    "questionNumber": 3,
    "question": "Which type of deed provides the greatest protection to the grantee?",
    "answerOptions": [
      {
        "text": "Quitclaim deed",
        "rationale": "A quitclaim deed offers the least protection, as it conveys only the interest the grantor has, if any, without any warranties.",
        "isCorrect": false
      },
      {
        "text": "Special warranty deed",
        "rationale": "A special warranty deed only warrants against defects that arose during the grantor's ownership.",
        "isCorrect": false
      },
      {
        "text": "General warranty deed",
        "rationale": "A general warranty deed provides the most protection to the grantee, as it includes warranties against defects that arose at any time, even before the grantor owned the property.",
        "isCorrect": true
      },
      {
        "text": "Bargain and sale deed",
        "rationale": "A bargain and sale deed implies that the grantor holds title and possession, but it does not offer the extensive warranties of a general warranty deed.",
        "isCorrect": false
      }
    ],
    "hint": "Consider which deed offers the most comprehensive set of promises from the seller to the buyer."
  },
  {
    "questionNumber": 4,
    "question": "In Georgia, what is the statutory right of redemption period after a tax sale?",
    "answerOptions": [
      {
        "text": "6 months",
        "rationale": "This is not the correct statutory period in Georgia.",
        "isCorrect": false
      },
      {
        "text": "1 year",
        "rationale": "The statutory right of redemption in Georgia after a tax sale is generally one year.",
        "isCorrect": true
      },
      {
        "text": "2 years",
        "rationale": "This is longer than the actual redemption period in Georgia.",
        "isCorrect": false
      },
      {
        "text": "There is no right of redemption after a tax sale in Georgia.",
        "rationale": "Georgia law does provide for a right of redemption.",
        "isCorrect": false
      }
    ],
    "hint": "This Georgia-specific law allows the original owner a specific amount of time to reclaim their property after it's sold for unpaid taxes."
  },
  {
    "questionNumber": 5,
    "question": "A broker in Georgia is required to maintain a trust account if they:",
    "answerOptions": [
      {
        "text": "Only handle their own properties.",
        "rationale": "A trust account is for other people's money, so a broker only handling their own properties would not need one.",
        "isCorrect": false
      },
      {
        "text": "Receive earnest money or other funds on behalf of a client.",
        "rationale": "A broker who holds funds belonging to others, such as earnest money, must maintain a trust account to keep those funds separate from their own.",
        "isCorrect": true
      },
      {
        "text": "Have been licensed for more than five years.",
        "rationale": "The requirement for a trust account is based on handling client funds, not the length of time a broker has been licensed.",
        "isCorrect": false
      },
      {
        "text": "Are a member of the National Association of Realtors.",
        "rationale": "Membership in a professional organization does not determine the legal requirement for a trust account.",
        "isCorrect": false
      }
    ],
    "hint": "The need for this special bank account is triggered when a broker handles money that doesn't belong to them."
  },
  {
    "questionNumber": 6,
    "question": "Which of the following would terminate an agency relationship?",
    "answerOptions": [
      {
        "text": "The agent showing the property to a prospective buyer.",
        "rationale": "Showing the property is part of the agent's duties and does not terminate the agency relationship.",
        "isCorrect": false
      },
      {
        "text": "The principal of the agency relationship dies.",
        "rationale": "The death of either the principal or the agent terminates the agency relationship by operation of law.",
        "isCorrect": true
      },
      {
        "text": "The agent presents an offer to the seller.",
        "rationale": "Presenting an offer is a key function of the agency relationship, not a reason for its termination.",
        "isCorrect": false
      },
      {
        "text": "The seller rejects an offer.",
        "rationale": "The rejection of an offer by the seller does not terminate the agency relationship; the agent's duty is to continue to market the property.",
        "isCorrect": false
      }
    ],
    "hint": "Consider events that would make it impossible for the agency agreement to continue."
  },
  {
    "questionNumber": 7,
    "question": "The Fair Housing Act prohibits discrimination based on all of the following EXCEPT:",
    "answerOptions": [
      {
        "text": "Race",
        "rationale": "Race is a protected class under the Fair Housing Act.",
        "isCorrect": false
      },
      {
        "text": "Religion",
        "rationale": "Religion is a protected class under the Fair Housing Act.",
        "isCorrect": false
      },
      {
        "text": "Marital status",
        "rationale": "While some state and local laws prohibit discrimination based on marital status, it is not a protected class under the federal Fair Housing Act.",
        "isCorrect": true
      },
      {
        "text": "Familial status",
        "rationale": "Familial status (having children under 18) is a protected class under the Fair Housing Act.",
        "isCorrect": false
      }
    ],
    "hint": "The federal law has a specific list of protected classes. Which one of these is not on that list?"
  },
  {
    "questionNumber": 8,
    "question": "In Georgia, a security deposit for a residential lease must be returned to the tenant within what timeframe after the termination of the lease?",
    "answerOptions": [
      {
        "text": "15 days",
        "rationale": "This is not the correct timeframe in Georgia.",
        "isCorrect": false
      },
      {
        "text": "30 days",
        "rationale": "Georgia law requires the landlord to return the security deposit within 30 days of the termination of the lease.",
        "isCorrect": true
      },
      {
        "text": "45 days",
        "rationale": "This is longer than the timeframe required by Georgia law.",
        "isCorrect": false
      },
      {
        "text": "60 days",
        "rationale": "This is significantly longer than the timeframe required by Georgia law.",
        "isCorrect": false
      }
    ],
    "hint": "Georgia has a specific law that dictates how long a landlord has to return a tenant's security deposit."
  },
  {
    "questionNumber": 9,
    "question": "What is the term for the illegal practice of a real estate agent inducing homeowners to sell their properties by making representations about the entry of a protected class into the neighborhood?",
    "answerOptions": [
      {
        "text": "Redlining",
        "rationale": "Redlining is the discriminatory practice of refusing to make loans in certain neighborhoods.",
        "isCorrect": false
      },
      {
        "text": "Steering",
        "rationale": "Steering is the illegal practice of channeling prospective buyers to or away from certain areas based on their protected class.",
        "isCorrect": false
      },
      {
        "text": "Blockbusting",
        "rationale": "Blockbusting is the illegal practice of inducing homeowners to sell by making representations about the entry or prospective entry of a protected class into the neighborhood.",
        "isCorrect": true
      },
      {
        "text": "Puffing",
        "rationale": "Puffing is the exaggeration of a property's features and is not related to fair housing laws.",
        "isCorrect": false
      }
    ],
    "hint": "This illegal tactic plays on the fears of homeowners to generate sales."
  },
  {
    "questionNumber": 10,
    "question": "A property is sold for $250,000 with a 6% commission. The listing broker and the selling broker agree to split the commission 50/50. How much will the selling broker receive?",
    "answerOptions": [
      {
        "text": "$3,750",
        "rationale": "This amount is incorrect. The calculation is $250,000 * 0.06 = $15,000, and half of that is $7,500.",
        "isCorrect": false
      },
      {
        "text": "$7,500",
        "rationale": "The total commission is $250,000 * 0.06 = $15,000.  The selling broker's 50% share is $7,500.",
        "isCorrect": true
      },
      {
        "text": "$15,000",
        "rationale": "This is the total commission, not the selling broker's share.",
        "isCorrect": false
      },
      {
        "text": "$12,500",
        "rationale": "This amount is incorrect.",
        "isCorrect": false
      }
    ],
    "hint": "First, calculate the total commission, and then determine the selling broker's share."
  },
  {
    "questionNumber": 11,
    "question": "Which of the following is an example of a voluntary lien?",
    "answerOptions": [
      {
        "text": "Mechanic's lien",
        "rationale": "A mechanic's lien is an involuntary lien placed on a property by someone who provided labor or materials but was not paid.",
        "isCorrect": false
      },
      {
        "text": "Property tax lien",
        "rationale": "A property tax lien is an involuntary lien placed on a property for unpaid property taxes.",
        "isCorrect": false
      },
      {
        "text": "Mortgage lien",
        "rationale": "A mortgage lien is a voluntary lien that a property owner agrees to when they take out a loan to purchase the property.",
        "isCorrect": true
      },
      {
        "text": "Judgment lien",
        "rationale": "A judgment lien is an involuntary lien that can be placed on a property as a result of a lawsuit.",
        "isCorrect": false
      }
    ],
    "hint": "Think about which type of lien is created with the property owner's consent."
  },
  {
    "questionNumber": 12,
    "question": "In Georgia, a real estate license is required for which of the following activities?",
    "answerOptions": [
      {
        "text": "An attorney-at-law acting in their professional capacity.",
        "rationale": "Attorneys are generally exempt from licensure requirements when performing their duties as an attorney.",
        "isCorrect": false
      },
      {
        "text": "A person acting under a power of attorney.",
        "rationale": "A person acting under a power of attorney from the owner is generally exempt.",
        "isCorrect": false
      },
      {
        "text": "A property manager who is an employee of the property owner.",
        "rationale": "An employee of the property owner is generally exempt.",
        "isCorrect": false
      },
      {
        "text": "A person who, for a fee, assists another in procuring prospects for the sale of real estate.",
        "rationale": "This activity, often referred to as 'bird-dogging,' requires a real estate license in Georgia.",
        "isCorrect": true
      }
    ],
    "hint": "Consider which activity involves representing others in a real estate transaction for compensation."
  },
  {
    "questionNumber": 13,
    "question": "The process by which the government takes private property for public use with just compensation is known as:",
    "answerOptions": [
      {
        "text": "Escheat",
        "rationale": "Escheat is the reversion of property to the state when an owner dies without a will and without heirs.",
        "isCorrect": false
      },
      {
        "text": "Condemnation",
        "rationale": "Condemnation is the legal process by which the government exercises its right of eminent domain.",
        "isCorrect": true
      },
      {
        "text": "Foreclosure",
        "rationale": "Foreclosure is the process by which a lender takes possession of a property after a borrower defaults on a loan.",
        "isCorrect": false
      },
      {
        "text": "Adverse possession",
        "rationale": "Adverse possession is the acquisition of title to another's property through open, notorious, hostile, and continuous use.",
        "isCorrect": false
      }
    ],
    "hint": "This is the legal action that accompanies the government's right of eminent domain."
  },
  {
    "questionNumber": 14,
    "question": "A buyer makes an offer on a property and the seller accepts.  Before closing, the property is destroyed by a fire.  What is the most likely outcome?",
    "answerOptions": [
      {
        "text": "The buyer must still purchase the property.",
        "rationale": "The destruction of the property would likely make the contract voidable.",
        "isCorrect": false
      },
      {
        "text": "The seller must rebuild the property and then sell it to the buyer.",
        "rationale": "The contract is typically for the property as it was when the offer was made.",
        "isCorrect": false
      },
      {
        "text": "The contract is voidable by the buyer.",
        "rationale": "In most cases, the risk of loss remains with the seller until closing, and the buyer would have the option to void the contract.",
        "isCorrect": true
      },
      {
        "text": "The buyer and seller must split the cost of the repairs.",
        "rationale": "The contract would likely be terminated, not renegotiated for repairs.",
        "isCorrect": false
      }
    ],
    "hint": "Consider who bears the risk of loss before the title to the property is transferred."
  },
  {
    "questionNumber": 15,
    "question": "Which of the following is a requirement for a valid real estate contract in Georgia?",
    "answerOptions": [
      {
        "text": "The contract must be notarized.",
        "rationale": "While notarization is good practice for some legal documents, it is not a requirement for a valid real estate contract in Georgia.",
        "isCorrect": false
      },
      {
        "text": "The contract must be in writing.",
        "rationale": "The Statute of Frauds requires that contracts for the sale of real estate be in writing to be enforceable.",
        "isCorrect": true
      },
      {
        "text": "The contract must be for a property valued at over $50,000.",
        "rationale": "The value of the property does not determine the requirement for a written contract.",
        "isCorrect": false
      },
      {
        "text": "The contract must be approved by the Georgia Real Estate Commission.",
        "rationale": "The Georgia Real Estate Commission does not approve individual real estate contracts.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the Statute of Frauds and its application to real estate transactions."
  },
  {
    "questionNumber": 16,
    "question": "A loan that allows the borrower to make interest-only payments for a specified period is known as a(n):",
    "answerOptions": [
      {
        "text": "Amortized loan",
        "rationale": "An amortized loan is one in which the principal is paid down over the life of the loan.",
        "isCorrect": false
      },
      {
        "text": "Balloon loan",
        "rationale": "A balloon loan has a large final payment, but it may or may not be interest-only.",
        "isCorrect": false
      },
      {
        "text": "Straight loan",
        "rationale": "A straight loan, also known as a term loan or an interest-only loan, allows the borrower to pay only the interest for a specified period.",
        "isCorrect": true
      },
      {
        "text": "Graduated payment mortgage",
        "rationale": "A graduated payment mortgage has payments that start low and then increase over time.",
        "isCorrect": false
      }
    ],
    "hint": "This type of loan is characterized by payments that only cover the interest, not the principal."
  },
  {
    "questionNumber": 17,
    "question": "What is the primary purpose of the Georgia Real Estate Commission?",
    "answerOptions": [
      {
        "text": "To set commission rates for real estate agents.",
        "rationale": "The Commission does not set commission rates; they are negotiable between the broker and the client.",
        "isCorrect": false
      },
      {
        "text": "To protect the public from harmful acts of real estate licensees.",
        "rationale": "The primary purpose of the Georgia Real Estate Commission is to protect the public by regulating the real estate industry and enforcing license law.",
        "isCorrect": true
      },
      {
        "text": "To represent the interests of real estate licensees.",
        "rationale": "While the Commission regulates licensees, its primary duty is to the public, not to the licensees.",
        "isCorrect": false
      },
      {
        "text": "To provide legal advice to real estate licensees.",
        "rationale": "The Commission does not provide legal advice; licensees should seek legal counsel when needed.",
        "isCorrect": false
      }
    ],
    "hint": "This state agency's main goal is to ensure that real estate professionals act ethically and legally."
  },
  {
    "questionNumber": 18,
    "question": "An appraiser is using the sales comparison approach to value a property.  The subject property has a swimming pool, but the comparable property does not.  How would the appraiser make the adjustment?",
    "answerOptions": [
      {
        "text": "Add the value of the swimming pool to the value of the subject property.",
        "rationale": "Adjustments are made to the comparable property, not the subject property.",
        "isCorrect": false
      },
      {
        "text": "Subtract the value of the swimming pool from the value of the subject property.",
        "rationale": "Adjustments are made to the comparable property, not the subject property.",
        "isCorrect": false
      },
      {
        "text": "Add the value of the swimming pool to the sales price of the comparable property.",
        "rationale": "If the subject property has a feature that the comparable property lacks, the value of that feature is added to the sales price of the comparable property to make it more like the subject property.",
        "isCorrect": true
      },
      {
        "text": "Subtract the value of the swimming pool from the sales price of the comparable property.",
        "rationale": "This would be the correct adjustment if the comparable property had a pool and the subject property did not.",
        "isCorrect": false
      }
    ],
    "hint": "The goal is to make the comparable property's value more similar to the subject property's value."
  },
  {
    "questionNumber": 19,
    "question": "In Georgia, what type of agency is presumed to exist between a broker and a client unless there is a written agreement to the contrary?",
    "answerOptions": [
      {
        "text": "Dual agency",
        "rationale": "Dual agency must be disclosed and agreed to in writing by all parties.",
        "isCorrect": false
      },
      {
        "text": "Designated agency",
        "rationale": "Designated agency is a specific type of agency that must be established by agreement.",
        "isCorrect": false
      },
      {
        "text": "Subagency",
        "rationale": "Subagency is not the presumed relationship in Georgia.",
        "isCorrect": false
      },
      {
        "text": "Transaction brokerage",
        "rationale": "The Brokerage Relationships in Real Estate Transactions Act (BRRETA) in Georgia establishes that a broker is acting as a transaction broker unless there is a written agreement creating a different agency relationship.",
        "isCorrect": true
      }
    ],
    "hint": "Georgia's BRRETA law sets a default relationship between a broker and a client."
  },
  {
    "questionNumber": 20,
    "question": "A property has a market value of $300,000 and is assessed at 40% of its market value.  If the tax rate is $2.50 per $100 of assessed value, what is the annual property tax?",
    "answerOptions": [
      {
        "text": "$3,000",
        "rationale": "The assessed value is $300,000 * 0.40 = $120,000.  The tax is ($120,000 / $100) * $2.50 = $3,000.",
        "isCorrect": true
      },
      {
        "text": "$7,500",
        "rationale": "This amount is incorrect. It appears to be based on the market value, not the assessed value.",
        "isCorrect": false
      },
      {
        "text": "$1,200",
        "rationale": "This amount is incorrect.",
        "isCorrect": false
      },
      {
        "text": "$4,800",
        "rationale": "This amount is incorrect.",
        "isCorrect": false
      }
    ],
    "hint": "First, find the assessed value, and then calculate the property tax based on that value."
  },
  {
    "questionNumber": 21,
    "question": "Which of the following would be considered a latent defect that must be disclosed by the seller?",
    "answerOptions": [
      {
        "text": "A large crack in the living room wall.",
        "rationale": "A large crack in the wall is a patent defect, meaning it is obvious and would be discovered upon a reasonable inspection.",
        "isCorrect": false
      },
      {
        "text": "A broken window.",
        "rationale": "A broken window is a patent defect.",
        "isCorrect": false
      },
      {
        "text": "A leaky roof that has been patched and is not currently leaking, but is likely to leak again in the future.",
        "rationale": "A latent defect is a hidden defect that would not be discovered by a reasonable inspection. A roof that has been patched but is still problematic would be a latent defect that the seller must disclose.",
        "isCorrect": true
      },
      {
        "text": "Peeling paint on the exterior of the house.",
        "rationale": "Peeling paint is a patent defect.",
        "isCorrect": false
      }
    ],
    "hint": "A latent defect is a hidden problem that the buyer wouldn't easily discover."
  },
  {
    "questionNumber": 22,
    "question": "A landlord in Georgia can evict a tenant for which of the following reasons?",
    "answerOptions": [
      {
        "text": "The tenant has requested repairs to the property.",
        "rationale": "It is illegal for a landlord to retaliate against a tenant for requesting repairs.",
        "isCorrect": false
      },
      {
        "text": "The tenant has joined a tenants' union.",
        "rationale": "It is illegal for a landlord to retaliate against a tenant for joining a tenants' union.",
        "isCorrect": false
      },
      {
        "text": "The tenant has failed to pay rent.",
        "rationale": "Failure to pay rent is a common and legal reason for eviction.",
        "isCorrect": true
      },
      {
        "text": "The tenant has a disability.",
        "rationale": "It is illegal to discriminate against a tenant based on a disability.",
        "isCorrect": false
      }
    ],
    "hint": "Eviction is a legal process, and it requires a valid reason as defined by law."
  },
  {
    "questionNumber": 23,
    "question": "A buyer wants to make an offer on a property but needs to sell their current home first. What type of clause should be included in the offer?",
    "answerOptions": [
      {
        "text": "Escape clause",
        "rationale": "An escape clause is for the seller, allowing them to accept another offer if certain conditions are not met.",
        "isCorrect": false
      },
      {
        "text": "Contingency clause",
        "rationale": "A contingency clause makes the contract dependent on a certain event, such as the sale of the buyer's current home.",
        "isCorrect": true
      },
      {
        "text": "Acceleration clause",
        "rationale": "An acceleration clause is in a loan agreement and allows the lender to demand immediate payment of the entire loan balance under certain circumstances.",
        "isCorrect": false
      },
      {
        "text": "Subordination clause",
        "rationale": "A subordination clause changes the priority of a lien.",
        "isCorrect": false
      }
    ],
    "hint": "This type of clause makes the contract dependent on a specific event happening."
  },
  {
    "questionNumber": 24,
    "question": "In Georgia, how many hours of post-license education are required for a new real estate salesperson within the first year of licensure?",
    "answerOptions": [
      {
        "text": "15 hours",
        "rationale": "This is not the correct number of hours.",
        "isCorrect": false
      },
      {
        "text": "25 hours",
        "rationale": "New salespersons in Georgia are required to complete a 25-hour post-license course within their first year of licensure.",
        "isCorrect": true
      },
      {
        "text": "36 hours",
        "rationale": "This is the number of hours required for continuing education every four years, not the post-license requirement.",
        "isCorrect": false
      },
      {
        "text": "45 hours",
        "rationale": "This is not the correct number of hours.",
        "isCorrect": false
      }
    ],
    "hint": "Georgia has a specific educational requirement for new licensees to complete after they get their license."
  },
  {
    "questionNumber": 25,
    "question": "The right of a property owner to have their land supported by the land of their neighbors is known as:",
    "answerOptions": [
      {
        "text": "Littoral rights",
        "rationale": "Littoral rights pertain to the use of water by the owner of land that abuts a lake or sea.",
        "isCorrect": false
      },
      {
        "text": "Riparian rights",
        "rationale": "Riparian rights pertain to the use of water by the owner of land that abuts a river or stream.",
        "isCorrect": false
      },
      {
        "text": "Easement rights",
        "rationale": "An easement is the right to use another's land for a specific purpose.",
        "isCorrect": false
      },
      {
        "text": "Lateral support",
        "rationale": "The right of lateral support is the right to have land supported by the adjoining land.",
        "isCorrect": true
      }
    ],
    "hint": "This legal concept is about the physical support of one piece of land by the land next to it."
  },
  {
    "questionNumber": 26,
    "question": "A developer is planning a new subdivision. The local zoning ordinance requires that each lot have a minimum of 10,000 square feet. This is an example of:",
    "answerOptions": [
      {
        "text": "A restrictive covenant",
        "rationale": "A restrictive covenant is a private agreement that restricts the use of real estate.",
        "isCorrect": false
      },
      {
        "text": "An easement",
        "rationale": "An easement is the right to use another's land for a specific purpose.",
        "isCorrect": false
      },
      {
        "text": "A building code",
        "rationale": "A building code sets standards for construction, not lot size.",
        "isCorrect": false
      },
      {
        "text": "Zoning",
        "rationale": "Zoning ordinances are local laws that regulate the use of land and buildings within a community.",
        "isCorrect": true
      }
    ],
    "hint": "This is a form of public land-use control."
  },
  {
    "questionNumber": 27,
    "question": "A property is listed for $200,000.  A buyer makes an offer of $190,000, which the seller rejects.  The seller then counters with an offer of $195,000.  At this point, what is the status of the buyer's original offer?",
    "answerOptions": [
      {
        "text": "It is still valid and can be accepted by the seller.",
        "rationale": "A counteroffer is a rejection of the original offer, so the original offer is no longer valid.",
        "isCorrect": false
      },
      {
        "text": "It has been terminated.",
        "rationale": "A counteroffer terminates the original offer.",
        "isCorrect": true
      },
      {
        "text": "It is in a pending status until the buyer responds to the counteroffer.",
        "rationale": "The original offer is no longer pending; the counteroffer is the new offer on the table.",
        "isCorrect": false
      },
      {
        "text": "It has been automatically accepted.",
        "rationale": "The original offer was rejected, not accepted.",
        "isCorrect": false
      }
    ],
    "hint": "A counteroffer has a specific legal effect on the offer that came before it."
  },
  {
    "questionNumber": 28,
    "question": "In Georgia, if a broker's license is suspended or revoked, what happens to the licenses of the salespersons affiliated with that broker?",
    "answerOptions": [
      {
        "text": "Their licenses are automatically suspended.",
        "rationale": "When a broker's license is suspended or revoked, the licenses of the affiliated salespersons are also suspended until they transfer their licenses to a new broker.",
        "isCorrect": true
      },
      {
        "text": "Their licenses are automatically revoked.",
        "rationale": "Their licenses are suspended, not revoked, and can be reactivated by affiliating with a new broker.",
        "isCorrect": false
      },
      {
        "text": "Their licenses are not affected.",
        "rationale": "The salespersons' licenses are directly affected by the status of their broker's license.",
        "isCorrect": false
      },
      {
        "text": "They are given a 90-day grace period to find a new broker.",
        "rationale": "The suspension is immediate, although they can begin the process of transferring their licenses right away.",
        "isCorrect": false
      }
    ],
    "hint": "A salesperson's license is dependent on their affiliation with a licensed broker."
  },
  {
    "questionNumber": 29,
    "question": "Which of the following would be considered a specific agent?",
    "answerOptions": [
      {
        "text": "A property manager who has a broad range of responsibilities for a property.",
        "rationale": "A property manager is typically a general agent.",
        "isCorrect": false
      },
      {
        "text": "A real estate agent who is hired to sell a single-family home.",
        "rationale": "A real estate agent hired to represent a client in a single transaction, such as the sale of a home, is a specific agent.",
        "isCorrect": true
      },
      {
        "text": "An employee who can act on behalf of their employer in all matters.",
        "rationale": "This would be an example of a universal agent.",
        "isCorrect": false
      },
      {
        "text": "A person with a power of attorney that allows them to handle all of their principal's affairs.",
        "rationale": "This is an example of a universal agent.",
        "isCorrect": false
      }
    ],
    "hint": "A specific agent is authorized to perform a particular act or transaction."
  },
  {
    "questionNumber": 30,
    "question": "The Truth in Lending Act (TILA), implemented by Regulation Z, requires lenders to disclose which of the following?",
    "answerOptions": [
      {
        "text": "The borrower's credit score.",
        "rationale": "While the credit score is used in the lending decision, TILA does not require its disclosure in this context.",
        "isCorrect": false
      },
      {
        "text": "The annual percentage rate (APR).",
        "rationale": "TILA requires the disclosure of the APR, which is the true cost of borrowing, expressed as a yearly rate.",
        "isCorrect": true
      },
      {
        "text": "The appraised value of the property.",
        "rationale": "The appraisal is a separate part of the loan process, and its disclosure is not the primary focus of TILA.",
        "isCorrect": false
      },
      {
        "text": "The name of the loan officer.",
        "rationale": "This is not a required disclosure under TILA.",
        "isCorrect": false
      }
    ],
    "hint": "This law is about making sure borrowers understand the true cost of their loan."
  },
  {
    "questionNumber": 31,
    "question": "A form of co-ownership in which two or more people own a property with the right of survivorship is known as:",
    "answerOptions": [
      {
        "text": "Tenancy in common",
        "rationale": "Tenancy in common does not include the right of survivorship; each owner's share passes to their heirs.",
        "isCorrect": false
      },
      {
        "text": "Joint tenancy",
        "rationale": "Joint tenancy includes the right of survivorship, meaning that when one owner dies, their interest in the property passes to the surviving owner(s).",
        "isCorrect": true
      },
      {
        "text": "Tenancy by the entirety",
        "rationale": "Tenancy by the entirety is a form of joint ownership for married couples that includes the right of survivorship, but Georgia is not a tenancy by the entirety state.",
        "isCorrect": false
      },
      {
        "text": "Community property",
        "rationale": "Community property is a system of ownership for married couples in some states, but Georgia is not a community property state.",
        "isCorrect": false
      }
    ],
    "hint": "This type of ownership has a key feature that determines what happens to the property when one of the owners dies."
  },
  {
    "questionNumber": 32,
    "question": "In Georgia, if a property is sold at a foreclosure sale for more than the amount of the outstanding debt, the excess funds belong to:",
    "answerOptions": [
      {
        "text": "The lender",
        "rationale": "The lender is only entitled to the amount of the outstanding debt.",
        "isCorrect": false
      },
      {
        "text": "The former owner",
        "rationale": "Any surplus funds from a foreclosure sale belong to the former owner.",
        "isCorrect": true
      },
      {
        "text": "The state of Georgia",
        "rationale": "The state does not receive the excess funds.",
        "isCorrect": false
      },
      {
        "text": "The new buyer",
        "rationale": "The new buyer is only entitled to the property, not any excess funds from the sale.",
        "isCorrect": false
      }
    ],
    "hint": "Consider who is entitled to the equity in the property after the debt has been paid."
  },
  {
    "questionNumber": 33,
    "question": "A buyer's agent has a duty to disclose which of the following to their client?",
    "answerOptions": [
      {
        "text": "That the seller is a close friend of the agent.",
        "rationale": "A buyer's agent has a fiduciary duty to disclose any potential conflicts of interest to their client.",
        "isCorrect": false
      },
      {
        "text": "The seller's lowest acceptable price, if known.",
        "rationale": "A buyer's agent does not owe a duty of confidentiality to the seller and should disclose any information that would be beneficial to their client.",
        "isCorrect": false
      },
      {
        "text": "Any material facts about the property that are known to the agent.",
        "rationale": "A buyer's agent has a duty to disclose all known material facts about the property to their client.",
        "isCorrect": false
      },
      {
        "text": "All of the above.",
        "rationale": "A buyer's agent has a fiduciary duty to their client that includes the disclosure of conflicts of interest, material facts, and any other information that would be relevant to the client's decision-making.",
        "isCorrect": true
      }
    ],
    "hint": "A buyer's agent has fiduciary duties to their client, which include loyalty, disclosure, and confidentiality."
  },
  {
    "questionNumber": 34,
    "question": "Which of the following is an example of an encumbrance on real property?",
    "answerOptions": [
      {
        "text": "A deed",
        "rationale": "A deed is a document that transfers title to real property, not an encumbrance.",
        "isCorrect": false
      },
      {
        "text": "A title insurance policy",
        "rationale": "A title insurance policy protects against encumbrances, but it is not an encumbrance itself.",
        "isCorrect": false
      },
      {
        "text": "An easement",
        "rationale": "An encumbrance is a claim, lien, charge, or liability attached to and binding on real property. An easement, which is the right to use another's land for a specific purpose, is a type of encumbrance.",
        "isCorrect": true
      },
      {
        "text": "A survey",
        "rationale": "A survey is a measurement of a property's boundaries, not an encumbrance.",
        "isCorrect": false
      }
    ],
    "hint": "An encumbrance is a right or interest in a property held by someone other than the owner."
  },
  {
    "questionNumber": 35,
    "question": "A homeowner has a mortgage with a fixed interest rate. If interest rates in the market increase, what will happen to the homeowner's monthly mortgage payment?",
    "answerOptions": [
      {
        "text": "It will increase.",
        "rationale": "With a fixed-rate mortgage, the interest rate and the principal and interest portion of the monthly payment remain the same for the life of the loan.",
        "isCorrect": false
      },
      {
        "text": "It will decrease.",
        "rationale": "With a fixed-rate mortgage, the interest rate and the principal and interest portion of the monthly payment remain the same for the life of the loan.",
        "isCorrect": false
      },
      {
        "text": "It will remain the same.",
        "rationale": "A fixed-rate mortgage protects the borrower from rising interest rates.",
        "isCorrect": true
      },
      {
        "text": "It will be recalculated based on the new market rate.",
        "rationale": "This would be the case with an adjustable-rate mortgage, not a fixed-rate mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "The key here is the term 'fixed interest rate'."
  },
  {
    "questionNumber": 36,
    "question": "In Georgia, the Real Estate Education, Research, and Recovery Fund is funded by:",
    "answerOptions": [
      {
        "text": "A portion of real estate license fees.",
        "rationale": "The Fund is financed by a portion of the fees paid by real estate licensees.",
        "isCorrect": true
      },
      {
        "text": "State income taxes.",
        "rationale": "The Fund is self-funded by the real estate industry, not by general tax revenue.",
        "isCorrect": false
      },
      {
        "text": "Fines paid by licensees for violations of the license law.",
        "rationale": "While fines are collected by the Commission, the primary source of funding for the Recovery Fund is license fees.",
        "isCorrect": false
      },
      {
        "text": "Private donations.",
        "rationale": "The Fund is not funded by donations.",
        "isCorrect": false
      }
    ],
    "hint": "This fund, which can be used to compensate consumers who have been harmed by the actions of a licensee, is paid for by the licensees themselves."
  },
  {
    "questionNumber": 37,
    "question": "The process of a tenant transferring their entire interest in a lease to another person is known as:",
    "answerOptions": [
      {
        "text": "Subletting",
        "rationale": "Subletting is when a tenant transfers only a portion of their interest in a lease to another person.",
        "isCorrect": false
      },
      {
        "text": "Assignment",
        "rationale": "An assignment is the transfer of the entire leasehold interest from the original tenant (the assignor) to a new tenant (the assignee).",
        "isCorrect": true
      },
      {
        "text": "Novation",
        "rationale": "Novation is the substitution of a new contract for an old one, which would release the original tenant from liability.",
        "isCorrect": false
      },
      {
        "text": "Estoppel",
        "rationale": "Estoppel is a legal principle that prevents someone from arguing something contrary to a claim they have previously made.",
        "isCorrect": false
      }
    ],
    "hint": "This is a complete transfer of the tenant's rights and obligations under the lease."
  },
  {
    "questionNumber": 38,
    "question": "A property owner has the right to control the use of their property, subject to governmental regulations. This is known as the right of:",
    "answerOptions": [
      {
        "text": "Possession",
        "rationale": "The right of possession is the right to occupy the property.",
        "isCorrect": false
      },
      {
        "text": "Enjoyment",
        "rationale": "The right of enjoyment is the right to use the property in any legal manner.",
        "isCorrect": true
      },
      {
        "text": "Exclusion",
        "rationale": "The right of exclusion is the right to keep others from entering or using the property.",
        "isCorrect": false
      },
      {
        "text": "Disposition",
        "rationale": "The right of disposition is the right to sell, will, transfer, or otherwise dispose of the property.",
        "isCorrect": false
      }
    ],
    "hint": "This is one of the bundle of legal rights that a property owner has."
  },
  {
    "questionNumber": 39,
    "question": "Which of the following is a potential consequence of a real estate agent engaging in the unauthorized practice of law?",
    "answerOptions": [
      {
        "text": "A promotion from their broker.",
        "rationale": "Engaging in the unauthorized practice of law is a serious offense and would not result in a promotion.",
        "isCorrect": false
      },
      {
        "text": "A bonus from the seller.",
        "rationale": "This is not a likely outcome.",
        "isCorrect": false
      },
      {
        "text": "Disciplinary action from the Georgia Real Estate Commission and potential criminal charges.",
        "rationale": "The unauthorized practice of law can lead to both administrative penalties from the Commission and criminal prosecution.",
        "isCorrect": true
      },
      {
        "text": "A commendation from the local bar association.",
        "rationale": "The local bar association would not commend someone for the unauthorized practice of law.",
        "isCorrect": false
      }
    ],
    "hint": "Real estate agents have a specific scope of practice, and going beyond that can have serious legal and professional repercussions."
  },
  {
    "questionNumber": 40,
    "question": "A property is 1.5 acres.  How many square feet is this?",
    "answerOptions": [
      {
        "text": "43,560 square feet",
        "rationale": "This is the number of square feet in one acre.",
        "isCorrect": false
      },
      {
        "text": "65,340 square feet",
        "rationale": "1.5 acres * 43,560 square feet/acre = 65,340 square feet.",
        "isCorrect": true
      },
      {
        "text": "87,120 square feet",
        "rationale": "This is the number of square feet in two acres.",
        "isCorrect": false
      },
      {
        "text": "21,780 square feet",
        "rationale": "This is the number of square feet in half an acre.",
        "isCorrect": false
      }
    ],
    "hint": "One acre is equal to 43,560 square feet."
  },
  {
    "questionNumber": 41,
    "question": "Which of the following environmental hazards is a colorless, odorless gas that can seep into a home from the ground and is a known carcinogen?",
    "answerOptions": [
      {
        "text": "Asbestos",
        "rationale": "Asbestos is a fibrous mineral that was used in building materials and is a known carcinogen, but it is not a gas.",
        "isCorrect": false
      },
      {
        "text": "Lead-based paint",
        "rationale": "Lead-based paint is a hazard, especially to children, but it is not a gas.",
        "isCorrect": false
      },
      {
        "text": "Radon",
        "rationale": "Radon is a radioactive gas that comes from the natural decay of uranium in soil, rock, and water and can accumulate in homes. It is a leading cause of lung cancer.",
        "isCorrect": true
      },
      {
        "text": "Mold",
        "rationale": "Mold is a type of fungus that can grow in homes and cause health problems, but it is not a colorless, odorless gas.",
        "isCorrect": false
      }
    ],
    "hint": "This hazard is often found in basements and can only be detected with a test kit."
  },
  {
    "questionNumber": 42,
    "question": "In Georgia, an exclusive right to sell listing agreement must include which of the following?",
    "answerOptions": [
      {
        "text": "A provision for automatic renewal.",
        "rationale": "Automatic renewal clauses are prohibited in Georgia listing agreements.",
        "isCorrect": false
      },
      {
        "text": "A specific termination date.",
        "rationale": "All exclusive listing agreements in Georgia must have a definite termination date.",
        "isCorrect": true
      },
      {
        "text": "The seller's agreement to pay the broker's commission even if the seller finds the buyer themselves.",
        "rationale": "This is a characteristic of an exclusive right to sell listing, but the question asks what must be included in the agreement, and the termination date is a legal requirement.",
        "isCorrect": false
      },
      {
        "text": "A minimum acceptable price.",
        "rationale": "While a listing price is included, a minimum acceptable price is not a required element of the agreement itself.",
        "isCorrect": false
      }
    ],
    "hint": "Georgia law requires that exclusive listing agreements have a clear end date."
  },
  {
    "questionNumber": 43,
    "question": "A loan's loan-to-value ratio (LTV) is the ratio of the:",
    "answerOptions": [
      {
        "text": "Loan amount to the property's appraised value or sale price, whichever is lower.",
        "rationale": "The LTV is a key metric used by lenders to assess the risk of a loan. It is calculated by dividing the loan amount by the lesser of the appraised value or the sale price.",
        "isCorrect": true
      },
      {
        "text": "Loan amount to the property's appraised value or sale price, whichever is higher.",
        "rationale": "Lenders use the lower of the appraised value or sale price to be more conservative in their risk assessment.",
        "isCorrect": false
      },
      {
        "text": "Down payment to the loan amount.",
        "rationale": "This is not the correct calculation for LTV.",
        "isCorrect": false
      },
      {
        "text": "Down payment to the sale price.",
        "rationale": "This is not the correct calculation for LTV.",
        "isCorrect": false
      }
    ],
    "hint": "This ratio is a measure of the risk a lender is taking on a loan."
  },
  {
    "questionNumber": 44,
    "question": "The fiduciary duty of obedience requires a real estate agent to:",
    "answerOptions": [
      {
        "text": "Obey all of the client's instructions, even if they are illegal.",
        "rationale": "An agent must obey the client's lawful instructions, but they must not obey any instructions that are illegal or unethical.",
        "isCorrect": false
      },
      {
        "text": "Obey all of the client's lawful instructions.",
        "rationale": "The duty of obedience requires the agent to act in good faith and obey the client's instructions, as long as they are legal and in accordance with the terms of the agency agreement.",
        "isCorrect": true
      },
      {
        "text": "Obey the instructions of the other party in the transaction.",
        "rationale": "The agent's duty is to their client, not the other party.",
        "isCorrect": false
      },
      {
        "text": "Obey the instructions of their broker, even if they conflict with the client's instructions.",
        "rationale": "The agent's primary duty is to their client, although they must also follow the lawful instructions of their broker.",
        "isCorrect": false
      }
    ],
    "hint": "This fiduciary duty is about following the client's lead, but it has its limits."
  },
  {
    "questionNumber": 45,
    "question": "In Georgia, who is responsible for conducting the closing of a real estate transaction?",
    "answerOptions": [
      {
        "text": "The real estate broker",
        "rationale": "While the broker is involved in the transaction, the actual closing is conducted by an attorney.",
        "isCorrect": false
      },
      {
        "text": "The lender",
        "rationale": "The lender provides the funds for the loan, but they do not conduct the closing.",
        "isCorrect": false
      },
      {
        "text": "An attorney",
        "rationale": "Georgia is an 'attorney state,' which means that the closing of a real estate transaction must be conducted by a licensed attorney.",
        "isCorrect": true
      },
      {
        "text": "A title company",
        "rationale": "In some states, title companies handle closings, but in Georgia, it must be an attorney.",
        "isCorrect": false
      }
    ],
    "hint": "Georgia is one of a handful of states that has a specific requirement for who can handle the final steps of a real estate transaction."
  },
  {
    "questionNumber": 46,
    "question": "A property is subject to a deed restriction that prohibits the owner from building a fence.  The local zoning ordinance allows fences.  Which takes precedence?",
    "answerOptions": [
      {
        "text": "The deed restriction",
        "rationale": "In general, the more restrictive of the two will take precedence.",
        "isCorrect": true
      },
      {
        "text": "The zoning ordinance",
        "rationale": "The deed restriction is a private agreement that is more restrictive than the zoning ordinance, so it will be enforced.",
        "isCorrect": false
      },
      {
        "text": "Whichever was recorded first",
        "rationale": "The order of recording does not determine which takes precedence in this case.",
        "isCorrect": false
      },
      {
        "text": "Neither, the owner can choose which to follow.",
        "rationale": "The owner must comply with both the deed restriction and the zoning ordinance.",
        "isCorrect": false
      }
    ],
    "hint": "When there is a conflict between a private restriction and a public one, the stricter of the two usually applies."
  },
  {
    "questionNumber": 47,
    "question": "Which of the following would be considered commingling?",
    "answerOptions": [
      {
        "text": "Depositing earnest money into a trust account.",
        "rationale": "This is the proper procedure for handling earnest money.",
        "isCorrect": false
      },
      {
        "text": "Depositing a commission check into a business account.",
        "rationale": "This is the proper procedure for handling a commission check.",
        "isCorrect": false
      },
      {
        "text": "Depositing a client's rent payment into the broker's personal checking account.",
        "rationale": "Commingling is the illegal act of mixing client funds with the broker's own personal or business funds.",
        "isCorrect": true
      },
      {
        "text": "Paying for a property inspection with a personal credit card.",
        "rationale": "This is not an example of commingling.",
        "isCorrect": false
      }
    ],
    "hint": "This is a serious violation of license law that involves mixing different types of funds in the same account."
  },
  {
    "questionNumber": 48,
    "question": "The Comprehensive Environmental Response, Compensation, and Liability Act (CERCLA) is also known as:",
    "answerOptions": [
      {
        "text": "The Clean Water Act",
        "rationale": "The Clean Water Act is a different environmental law that regulates water pollution.",
        "isCorrect": false
      },
      {
        "text": "The Superfund Act",
        "rationale": "CERCLA created a 'Superfund' to clean up hazardous waste sites.",
        "isCorrect": true
      },
      {
        "text": "The Safe Drinking Water Act",
        "rationale": "The Safe Drinking Water Act is a different environmental law that regulates drinking water quality.",
        "isCorrect": false
      },
      {
        "text": "The National Environmental Policy Act",
        "rationale": "The National Environmental Policy Act is a different environmental law that requires federal agencies to assess the environmental impact of their actions.",
        "isCorrect": false
      }
    ],
    "hint": "This federal law was enacted to address the problem of abandoned hazardous waste sites."
  },
  {
    "questionNumber": 49,
    "question": "A buyer and seller have a verbal agreement for the sale of a property.  This agreement is:",
    "answerOptions": [
      {
        "text": "Valid and enforceable.",
        "rationale": "The Statute of Frauds requires that real estate contracts be in writing to be enforceable.",
        "isCorrect": false
      },
      {
        "text": "Valid but unenforceable.",
        "rationale": "A verbal agreement for the sale of real estate is not enforceable under the Statute of Frauds.",
        "isCorrect": true
      },
      {
        "text": "Void",
        "rationale": "The agreement is not necessarily void, but it is unenforceable.",
        "isCorrect": false
      },
      {
        "text": "Illegal",
        "rationale": "The agreement is not illegal, but it is not enforceable in a court of law.",
        "isCorrect": false
      }
    ],
    "hint": "The Statute of Frauds has a very specific requirement for real estate contracts."
  },
  {
    "questionNumber": 50,
    "question": "In Georgia, a landlord who wants to terminate a tenancy-at-will must give the tenant how much notice?",
    "answerOptions": [
      {
        "text": "30 days",
        "rationale": "For a tenancy-at-will, the landlord must give the tenant 60 days' notice to terminate the tenancy.",
        "isCorrect": false
      },
      {
        "text": "60 days",
        "rationale": "In Georgia, a landlord must provide 60 days' notice to terminate a tenancy-at-will.",
        "isCorrect": true
      },
      {
        "text": "90 days",
        "rationale": "This is longer than the required notice period.",
        "isCorrect": false
      },
      {
        "text": "A reasonable amount of time.",
        "rationale": "The notice period is specified by statute.",
        "isCorrect": false
      }
    ],
    "hint": "Georgia law has specific notice requirements for ending a tenancy that does not have a set end date."
  },
  {
    "questionNumber": 51,
    "question": "The cost approach to appraisal is most likely to be used for which of the following properties?",
    "answerOptions": [
      {
        "text": "A single-family home in a new subdivision.",
        "rationale": "The sales comparison approach is typically used for single-family homes.",
        "isCorrect": false
      },
      {
        "text": "A condominium unit.",
        "rationale": "The sales comparison approach is typically used for condominiums.",
        "isCorrect": false
      },
      {
        "text": "A new school building.",
        "rationale": "The cost approach is most reliable for unique properties that do not have comparable sales, such as schools, churches, and government buildings.",
        "isCorrect": true
      },
      {
        "text": "A shopping center.",
        "rationale": "The income approach is typically used for income-producing properties like shopping centers.",
        "isCorrect": false
      }
    ],
    "hint": "This appraisal method is best for properties that are not typically sold on the open market."
  },
  {
    "questionNumber": 52,
    "question": "A buyer is purchasing a home for $400,000 and is getting an 80% LTV loan. What is the amount of the down payment?",
    "answerOptions": [
      {
        "text": "$40,000",
        "rationale": "This is a 10% down payment.",
        "isCorrect": false
      },
      {
        "text": "$80,000",
        "rationale": "If the loan is for 80% of the purchase price, the down payment is the remaining 20%. $400,000 * 0.20 = $80,000.",
        "isCorrect": true
      },
      {
        "text": "$320,000",
        "rationale": "This is the amount of the loan, not the down payment.",
        "isCorrect": false
      },
      {
        "text": "$20,000",
        "rationale": "This is a 5% down payment.",
        "isCorrect": false
      }
    ],
    "hint": "The down payment is the portion of the purchase price that is not covered by the loan."
  },
  {
    "questionNumber": 53,
    "question": "Which of the following would be considered an example of puffing?",
    "answerOptions": [
      {
        "text": "Stating that the property has 2,000 square feet when it actually has 1,800 square feet.",
        "rationale": "This is a misrepresentation of a material fact, not puffing.",
        "isCorrect": false
      },
      {
        "text": "Stating that the property has 'the most beautiful view in the city.'",
        "rationale": "Puffing is the exaggeration of a property's features in a way that is not a statement of fact.  This is a subjective opinion and would be considered puffing.",
        "isCorrect": true
      },
      {
        "text": "Failing to disclose that the property has a history of flooding.",
        "rationale": "This is a failure to disclose a material fact, not puffing.",
        "isCorrect": false
      },
      {
        "text": "Stating that the roof is new when it is 10 years old.",
        "rationale": "This is a misrepresentation of a material fact, not puffing.",
        "isCorrect": false
      }
    ],
    "hint": "Puffing is a statement of opinion, not a statement of fact."
  },
  {
    "questionNumber": 54,
    "question": "In Georgia, which of the following is an unfair trade practice?",
    "answerOptions": [
      {
        "text": "Representing a client in a real estate transaction.",
        "rationale": "This is the primary function of a real estate agent.",
        "isCorrect": false
      },
      {
        "text": "Charging a commission for services rendered.",
        "rationale": "This is how real estate agents are compensated.",
        "isCorrect": false
      },
      {
        "text": "Placing a for sale sign on a property without the owner's written consent.",
        "rationale": "This is a violation of Georgia license law and is considered an unfair trade practice.",
        "isCorrect": true
      },
      {
        "text": "Advertising a property for sale.",
        "rationale": "This is a standard practice in real estate.",
        "isCorrect": false
      }
    ],
    "hint": "Georgia's license law has specific rules about how agents can market a property."
  },
  {
    "questionNumber": 55,
    "question": "A contract that has been fully performed by both parties is known as a(n):",
    "answerOptions": [
      {
        "text": "Executed contract",
        "rationale": "An executed contract is one that has been fully performed.",
        "isCorrect": true
      },
      {
        "text": "Executory contract",
        "rationale": "An executory contract is one in which some or all of the obligations have not yet been performed.",
        "isCorrect": false
      },
      {
        "text": "Voidable contract",
        "rationale": "A voidable contract is one that can be legally avoided by one of the parties.",
        "isCorrect": false
      },
      {
        "text": "Unilateral contract",
        "rationale": "A unilateral contract is one in which one party makes a promise in exchange for an act.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the status of the contract after all of the promises have been fulfilled."
  },
  {
    "questionNumber": 56,
    "question": "The process of land building up from the deposit of soil by a river or stream is known as:",
    "answerOptions": [
      {
        "text": "Erosion",
        "rationale": "Erosion is the gradual wearing away of land.",
        "isCorrect": false
      },
      {
        "text": "Accretion",
        "rationale": "Accretion is the process of land being added to a property by the gradual deposit of soil.",
        "isCorrect": true
      },
      {
        "text": "Avulsion",
        "rationale": "Avulsion is the sudden loss of land, such as by a flood.",
        "isCorrect": false
      },
      {
        "text": "Reliction",
        "rationale": "Reliction is the increase in land that occurs when water recedes.",
        "isCorrect": false
      }
    ],
    "hint": "This is a natural process that can add to a property's size over time."
  },
  {
    "questionNumber": 57,
    "question": "A buyer makes an offer on a property and gives the seller's agent a $5,000 earnest money check. The seller's agent should:",
    "answerOptions": [
      {
        "text": "Deposit the check into their personal account and write a check to the seller for the same amount.",
        "rationale": "This is commingling and is illegal.",
        "isCorrect": false
      },
      {
        "text": "Give the check to the seller to hold until closing.",
        "rationale": "The earnest money should be deposited into a trust account.",
        "isCorrect": false
      },
      {
        "text": "Deposit the check into the broker's trust account as soon as practically possible.",
        "rationale": "Earnest money must be deposited into a trust account in a timely manner.",
        "isCorrect": true
      },
      {
        "text": "Cash the check and hold the cash in a safe place until closing.",
        "rationale": "This is a violation of license law.",
        "isCorrect": false
      }
    ],
    "hint": "There are strict rules about how earnest money must be handled."
  },
  {
    "questionNumber": 58,
    "question": "A person who is appointed by a court to manage the estate of a deceased person is known as a(n):",
    "answerOptions": [
      {
        "text": "Executor",
        "rationale": "An executor is named in a will to carry out the terms of the will.",
        "isCorrect": false
      },
      {
        "text": "Administrator",
        "rationale": "An administrator is appointed by a court to manage the estate of a person who died without a will.",
        "isCorrect": true
      },
      {
        "text": "Trustee",
        "rationale": "A trustee is a person who holds property in trust for another.",
        "isCorrect": false
      },
      {
        "text": "Guardian",
        "rationale": "A guardian is a person who is legally responsible for the care of another person or their property.",
        "isCorrect": false
      }
    ],
    "hint": "This person is appointed by the court when someone dies without a will."
  },
  {
    "questionNumber": 59,
    "question": "Which of the following is a type of freehold estate?",
    "answerOptions": [
      {
        "text": "Estate for years",
        "rationale": "An estate for years is a leasehold estate with a definite beginning and end.",
        "isCorrect": false
      },
      {
        "text": "Fee simple absolute",
        "rationale": "A fee simple absolute is the highest form of ownership and is a type of freehold estate.",
        "isCorrect": true
      },
      {
        "text": "Periodic tenancy",
        "rationale": "A periodic tenancy is a leasehold estate that automatically renews for successive periods.",
        "isCorrect": false
      },
      {
        "text": "Tenancy at will",
        "rationale": "A tenancy at will is a leasehold estate that can be terminated by either party at any time.",
        "isCorrect": false
      }
    ],
    "hint": "A freehold estate is an estate of ownership, as opposed to a leasehold estate, which is an estate of possession."
  },
  {
    "questionNumber": 60,
    "question": "A broker has an exclusive agency listing with a seller. If the seller finds a buyer for the property on their own, the broker is entitled to:",
    "answerOptions": [
      {
        "text": "The full commission.",
        "rationale": "With an exclusive agency listing, the broker is not entitled to a commission if the seller finds the buyer.",
        "isCorrect": false
      },
      {
        "text": "Half of the commission.",
        "rationale": "This is not the correct arrangement for an exclusive agency listing.",
        "isCorrect": false
      },
      {
        "text": "No commission.",
        "rationale": "An exclusive agency listing allows the seller to sell the property themselves without paying a commission.",
        "isCorrect": true
      },
      {
        "text": "A predetermined marketing fee.",
        "rationale": "The broker is not entitled to any compensation in this scenario.",
        "isCorrect": false
      }
    ],
    "hint": "The type of listing agreement determines whether the broker gets paid if the seller finds the buyer."
  },
  {
    "questionNumber": 61,
    "question": "The Georgia Property Owners' Association Act (POAA) applies to which of the following?",
    "answerOptions": [
      {
        "text": "All residential properties in Georgia.",
        "rationale": "The POAA only applies to properties that are part of a homeowners' association that has opted in to the Act.",
        "isCorrect": false
      },
      {
        "text": "Condominiums",
        "rationale": "Condominiums are governed by the Georgia Condominium Act, not the POAA.",
        "isCorrect": false
      },
      {
        "text": "Homeowners' associations that have submitted to the Act.",
        "rationale": "The POAA is a voluntary law that provides a framework for the operation of homeowners' associations that choose to be governed by it.",
        "isCorrect": true
      },
      {
        "text": "Commercial properties",
        "rationale": "The POAA applies to residential properties, not commercial properties.",
        "isCorrect": false
      }
    ],
    "hint": "This Georgia law is specific to certain types of planned communities."
  },
  {
    "questionNumber": 62,
    "question": "A buyer is getting a loan from a lender. The lender requires the buyer to pay for private mortgage insurance (PMI). This is most likely because:",
    "answerOptions": [
      {
        "text": "The buyer's credit score is low.",
        "rationale": "While a low credit score can affect the terms of a loan, PMI is typically required when the down payment is less than 20%.",
        "isCorrect": false
      },
      {
        "text": "The buyer is making a down payment of less than 20%.",
        "rationale": "PMI protects the lender in case the borrower defaults on the loan and is usually required when the LTV is greater than 80%.",
        "isCorrect": true
      },
      {
        "text": "The property is in a flood zone.",
        "rationale": "Flood insurance would be required in this case, not PMI.",
        "isCorrect": false
      },
      {
        "text": "The loan is an FHA loan.",
        "rationale": "FHA loans have their own mortgage insurance premium (MIP), which is different from PMI.",
        "isCorrect": false
      }
    ],
    "hint": "PMI is a type of insurance that protects the lender, and it is associated with the size of the down payment."
  },
  {
    "questionNumber": 63,
    "question": "The act of recording a deed provides which of the following?",
    "answerOptions": [
      {
        "text": "Actual notice",
        "rationale": "Actual notice is when a person has direct knowledge of a fact.",
        "isCorrect": false
      },
      {
        "text": "Constructive notice",
        "rationale": "Recording a deed in the public records provides constructive notice to the world of the new owner's interest in the property.",
        "isCorrect": true
      },
      {
        "text": "A guarantee of clear title.",
        "rationale": "Recording a deed does not guarantee clear title; it only gives notice of the transfer.",
        "isCorrect": false
      },
      {
        "text": "Protection against all future claims.",
        "rationale": "While recording a deed provides protection against subsequent claims, it does not protect against all possible claims.",
        "isCorrect": false
      }
    ],
    "hint": "This is a form of legal notice that is accomplished by making a document part of the public record."
  },
  {
    "questionNumber": 64,
    "question": "A property is sold for $350,000.  The transfer tax in Georgia is $1.00 per $1,000 of the sale price.  What is the amount of the transfer tax?",
    "answerOptions": [
      {
        "text": "$35",
        "rationale": "The calculation is ($350,000 / $1,000) * $1.00 = $350.",
        "isCorrect": false
      },
      {
        "text": "$350",
        "rationale": "The transfer tax is calculated as ($350,000 / $1,000) * $1.00 = $350.",
        "isCorrect": true
      },
      {
        "text": "$3,500",
        "rationale": "This amount is incorrect.",
        "isCorrect": false
      },
      {
        "text": "$1,000",
        "rationale": "This amount is incorrect.",
        "isCorrect": false
      }
    ],
    "hint": "Georgia's transfer tax has a specific formula based on the sale price of the property."
  },
  {
    "questionNumber": 65,
    "question": "A contract is entered into by a person who is under the age of 18.  The contract is:",
    "answerOptions": [
      {
        "text": "Void",
        "rationale": "A contract with a minor is not automatically void.",
        "isCorrect": false
      },
      {
        "text": "Voidable by the minor.",
        "rationale": "A contract entered into by a minor is voidable at the option of the minor.",
        "isCorrect": true
      },
      {
        "text": "Voidable by the adult party.",
        "rationale": "Only the minor has the option to void the contract.",
        "isCorrect": false
      },
      {
        "text": "Valid and enforceable.",
        "rationale": "The minor has the right to disaffirm the contract.",
        "isCorrect": false
      }
    ],
    "hint": "The law gives special protection to minors who enter into contracts."
  },
  {
    "questionNumber": 66,
    "question": "A property management agreement should include all of the following EXCEPT:",
    "answerOptions": [
      {
        "text": "The property manager's compensation.",
        "rationale": "The agreement must clearly state how the property manager will be compensated.",
        "isCorrect": false
      },
      {
        "text": "The property manager's authority and responsibilities.",
        "rationale": "The agreement must define the scope of the property manager's duties.",
        "isCorrect": false
      },
      {
        "text": "The names of the tenants.",
        "rationale": "The names of the tenants would not be known at the time the property management agreement is signed and would be part of the individual lease agreements.",
        "isCorrect": true
      },
      {
        "text": "The term of the agreement.",
        "rationale": "The agreement must have a definite beginning and end date.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what information would be included in the agreement between the property owner and the property manager."
  },
  {
    "questionNumber": 67,
    "question": "The right of a person to have the first opportunity to purchase or lease a property is known as:",
    "answerOptions": [
      {
        "text": "An option",
        "rationale": "An option is a contract that gives one party the right to buy a property at a specified price within a specified time.",
        "isCorrect": false
      },
      {
        "text": "A right of first refusal",
        "rationale": "A right of first refusal gives a person the opportunity to match an offer that the owner has received from another party.",
        "isCorrect": true
      },
      {
        "text": "A lease with an option to buy",
        "rationale": "This is a specific type of lease agreement.",
        "isCorrect": false
      },
      {
        "text": "A contract for deed",
        "rationale": "A contract for deed is a type of seller financing.",
        "isCorrect": false
      }
    ],
    "hint": "This gives someone the chance to buy a property before the owner can sell it to someone else."
  },
  {
    "questionNumber": 68,
    "question": "A broker is representing both the buyer and the seller in the same transaction. This is known as:",
    "answerOptions": [
      {
        "text": "Designated agency",
        "rationale": "Designated agency is when a broker appoints two of their agents to represent the buyer and the seller separately.",
        "isCorrect": false
      },
      {
        "text": "Dual agency",
        "rationale": "Dual agency occurs when a broker represents both the buyer and the seller in the same transaction.  It must be disclosed and agreed to in writing by both parties.",
        "isCorrect": true
      },
      {
        "text": "Subagency",
        "rationale": "Subagency is when an agent from one brokerage represents a client of another brokerage.",
        "isCorrect": false
      },
      {
        "text": "Transaction brokerage",
        "rationale": "A transaction broker does not represent either party as an agent, but instead facilitates the transaction.",
        "isCorrect": false
      }
    ],
    "hint": "This type of agency has the potential for conflicts of interest and requires special disclosure."
  },
  {
    "questionNumber": 69,
    "question": "A legal description that uses monuments, distances, and directions is known as:",
    "answerOptions": [
      {
        "text": "A rectangular survey system description",
        "rationale": "The rectangular survey system uses townships, ranges, and sections.",
        "isCorrect": false
      },
      {
        "text": "A lot and block description",
        "rationale": "The lot and block system is used for subdivisions and references a recorded plat map.",
        "isCorrect": false
      },
      {
        "text": "A metes and bounds description",
        "rationale": "A metes and bounds description starts at a point of beginning and proceeds around the property's boundaries by reference to monuments, distances, and directions.",
        "isCorrect": true
      },
      {
        "text": "A geodetic survey system description",
        "rationale": "The geodetic survey system uses benchmarks to describe the elevation of a property.",
        "isCorrect": false
      }
    ],
    "hint": "This is the oldest method of describing land."
  },
  {
    "questionNumber": 70,
    "question": "A homeowner has a mortgage that is assumable.  This means that:",
    "answerOptions": [
      {
        "text": "The homeowner can pay off the loan at any time without a penalty.",
        "rationale": "This is related to a prepayment penalty clause, not assumability.",
        "isCorrect": false
      },
      {
        "text": "A new buyer can take over the seller's existing mortgage.",
        "rationale": "An assumable mortgage allows a buyer to assume the seller's loan, typically with the lender's approval.",
        "isCorrect": true
      },
      {
        "text": "The interest rate on the loan can be adjusted.",
        "rationale": "This is a feature of an adjustable-rate mortgage, not necessarily an assumable mortgage.",
        "isCorrect": false
      },
      {
        "text": "The loan is insured by the federal government.",
        "rationale": "While some government-insured loans are assumable, not all are, and assumability is a separate feature.",
        "isCorrect": false
      }
    ],
    "hint": "This feature of a loan can be attractive to a buyer, especially when interest rates are rising."
  },
  {
    "questionNumber": 71,
    "question": "A property is listed with a broker, but the seller is also trying to sell the property themselves.  If the seller finds a buyer, they will not owe the broker a commission. This is an example of a(n):",
    "answerOptions": [
      {
        "text": "Open listing",
        "rationale": "An open listing allows the seller to list the property with multiple brokers and also sell it themselves.",
        "isCorrect": false
      },
      {
        "text": "Exclusive right to sell listing",
        "rationale": "With an exclusive right to sell listing, the broker gets a commission no matter who finds the buyer.",
        "isCorrect": false
      },
      {
        "text": "Exclusive agency listing",
        "rationale": "An exclusive agency listing gives the broker the exclusive right to market the property, but it allows the seller to sell the property themselves without paying a commission.",
        "isCorrect": true
      },
      {
        "text": "Net listing",
        "rationale": "A net listing is one in which the broker's commission is any amount of money over a specified net amount to the seller. Net listings are illegal in many states, including Georgia.",
        "isCorrect": false
      }
    ],
    "hint": "This type of listing agreement is a compromise between giving a broker the exclusive right to sell and allowing the seller to also try to sell the property."
  },
  {
    "questionNumber": 72,
    "question": "A deed is used to transfer title to real property.  What is the document used to transfer title to personal property?",
    "answerOptions": [
      {
        "text": "A bill of sale",
        "rationale": "A bill of sale is a legal document that transfers ownership of personal property from a seller to a buyer.",
        "isCorrect": true
      },
      {
        "text": "A mortgage",
        "rationale": "A mortgage is a loan used to purchase real property.",
        "isCorrect": false
      },
      {
        "text": "A lease",
        "rationale": "A lease is a contract for the possession of property.",
        "isCorrect": false
      },
      {
        "text": "An appraisal",
        "rationale": "An appraisal is an estimate of a property's value.",
        "isCorrect": false
      }
    ],
    "hint": "This document is like a receipt for the sale of goods."
  },
  {
    "questionNumber": 73,
    "question": "A buyer is purchasing a property and the closing is on June 15th.  The seller has already paid the property taxes for the entire year, which are $2,400.  How will the property taxes be prorated at closing?",
    "answerOptions": [
      {
        "text": "The buyer will owe the seller $1,300.",
        "rationale": "The seller is responsible for the property taxes from January 1st to June 14th. The buyer is responsible from June 15th to December 31st.  $2,400 / 12 months = $200/month.  The buyer will owe the seller for 6.5 months of property taxes, or $1,300.",
        "isCorrect": true
      },
      {
        "text": "The seller will owe the buyer $1,300.",
        "rationale": "The buyer owes the seller for the portion of the year that they will own the property.",
        "isCorrect": false
      },
      {
        "text": "The buyer will owe the seller $1,100.",
        "rationale": "This amount is incorrect. The calculation should be based on 6.5 months.",
        "isCorrect": false
      },
      {
        "text": "The seller will owe the buyer $1,100.",
        "rationale": "This amount is incorrect. The calculation should be based on 6.5 months.",
        "isCorrect": false
      }
    ],
    "hint": "Proration is the process of dividing expenses between the buyer and the seller at closing."
  },
  {
    "questionNumber": 74,
    "question": "The Americans with Disabilities Act (ADA) applies to which of the following?",
    "answerOptions": [
      {
        "text": "All residential properties",
        "rationale": "The ADA applies to public accommodations and commercial facilities, not strictly residential properties unless they have a public component (e.g., a rental office).",
        "isCorrect": false
      },
      {
        "text": "Single-family homes",
        "rationale": "The ADA does not apply to single-family homes.",
        "isCorrect": false
      },
      {
        "text": "Public accommodations and commercial facilities",
        "rationale": "The ADA prohibits discrimination against people with disabilities in public accommodations and commercial facilities.",
        "isCorrect": true
      },
      {
        "text": "Vacant land",
        "rationale": "The ADA does not apply to vacant land, unless it is being developed for a public accommodation.",
        "isCorrect": false
      }
    ],
    "hint": "This federal law is about ensuring access for people with disabilities in public places."
  },
  {
    "questionNumber": 75,
    "question": "A homeowner has an easement appurtenant to cross their neighbor's property to get to a lake. The homeowner sells their property to a new owner. What happens to the easement?",
    "answerOptions": [
      {
        "text": "It is terminated.",
        "rationale": "An easement appurtenant runs with the land, meaning it is transferred to the new owner.",
        "isCorrect": false
      },
      {
        "text": "It is transferred to the new owner.",
        "rationale": "An easement appurtenant is a right that is attached to the land and benefits the owner of that land.  When the land is sold, the easement is transferred to the new owner.",
        "isCorrect": true
      },
      {
        "text": "The new owner must renegotiate the easement with the neighbor.",
        "rationale": "The easement is already in place and does not need to be renegotiated.",
        "isCorrect": false
      },
      {
        "text": "It becomes an easement in gross.",
        "rationale": "The easement remains an easement appurtenant.",
        "isCorrect": false
      }
    ],
    "hint": "An easement appurtenant is a right that is attached to the land, not to the owner of the land."
  },
  {
    "questionNumber": 76,
    "question": "A buyer is interested in a property but is concerned about potential environmental issues. The buyer should:",
    "answerOptions": [
      {
        "text": "Rely on the seller's disclosures.",
        "rationale": "While the seller must disclose known environmental issues, the buyer should not rely solely on the seller's disclosures.",
        "isCorrect": false
      },
      {
        "text": "Have a Phase I Environmental Site Assessment performed.",
        "rationale": "A Phase I ESA is a report that identifies potential or existing environmental contamination liabilities. It is a good idea for a buyer to have one done if they are concerned about environmental issues.",
        "isCorrect": true
      },
      {
        "text": "Ask the real estate agent for their opinion.",
        "rationale": "A real estate agent is not an expert on environmental issues and should not give their opinion.",
        "isCorrect": false
      },
      {
        "text": "Ignore the issue, as it is the seller's responsibility.",
        "rationale": "The buyer has a responsibility to perform due diligence and investigate any potential issues with the property.",
        "isCorrect": false
      }
    ],
    "hint": "A buyer should take proactive steps to investigate any concerns they have about a property."
  },
  {
    "questionNumber": 77,
    "question": "A real estate agent is working with a buyer who is a member of a protected class.  The agent only shows the buyer properties in neighborhoods where that protected class is the majority. This is an example of:",
    "answerOptions": [
      {
        "text": "Redlining",
        "rationale": "Redlining is the discriminatory practice of refusing to make loans in certain neighborhoods.",
        "isCorrect": false
      },
      {
        "text": "Steering",
        "rationale": "Steering is the illegal practice of channeling prospective buyers to or away from certain areas based on their protected class.",
        "isCorrect": true
      },
      {
        "text": "Blockbusting",
        "rationale": "Blockbusting is the illegal practice of inducing homeowners to sell by making representations about the entry or prospective entry of a protected class into the neighborhood.",
        "isCorrect": false
      },
      {
        "text": "Good customer service.",
        "rationale": "This is a violation of fair housing laws and is not good customer service.",
        "isCorrect": false
      }
    ],
    "hint": "This is a form of illegal discrimination that limits a buyer's housing choices."
  },
  {
    "questionNumber": 78,
    "question": "A property has a net operating income (NOI) of $50,000 and the capitalization rate is 8%. What is the value of the property?",
    "answerOptions": [
      {
        "text": "$400,000",
        "rationale": "Value = NOI / Capitalization Rate. $50,000 / 0.08 = $625,000.",
        "isCorrect": false
      },
      {
        "text": "$625,000",
        "rationale": "Value = NOI / Capitalization Rate. $50,000 / 0.08 = $625,000.",
        "isCorrect": true
      },
      {
        "text": "$500,000",
        "rationale": "This is not the correct calculation.",
        "isCorrect": false
      },
      {
        "text": "$4,000,000",
        "rationale": "This is not the correct calculation.",
        "isCorrect": false
      }
    ],
    "hint": "The formula for the income approach to appraisal is Value = NOI / Capitalization Rate."
  },
  {
    "questionNumber": 79,
    "question": "The Georgia Stigmatized Property Act states that a real estate agent:",
    "answerOptions": [
      {
        "text": "Must disclose if a property was the site of a homicide or suicide.",
        "rationale": "The Act states that agents are not required to disclose this information.",
        "isCorrect": false
      },
      {
        "text": "Is not required to disclose if a property was the site of a homicide, suicide, or other felony, or if it was occupied by a person with a communicable disease.",
        "rationale": "The Act protects agents from liability for failing to disclose this type of information.",
        "isCorrect": true
      },
      {
        "text": "Must disclose if a property is haunted.",
        "rationale": "The Act does not address the issue of haunted properties.",
        "isCorrect": false
      },
      {
        "text": "Must disclose if a previous occupant had AIDS.",
        "rationale": "This would be a violation of fair housing laws.",
        "isCorrect": false
      }
    ],
    "hint": "This Georgia law addresses the disclosure of information that may not be a physical defect but could still affect a buyer's decision."
  },
  {
    "questionNumber": 80,
    "question": "The right of the government to enact laws, ordinances, and regulations to protect the public health, safety, and welfare is known as:",
    "answerOptions": [
      {
        "text": "Eminent domain",
        "rationale": "Eminent domain is the right of the government to take private property for public use.",
        "isCorrect": false
      },
      {
        "text": "Police power",
        "rationale": "Police power is the government's authority to regulate private affairs to protect the public good.  Zoning, building codes, and environmental regulations are examples of the exercise of police power.",
        "isCorrect": true
      },
      {
        "text": "Taxation",
        "rationale": "Taxation is the right of the government to charge a fee on real property to raise funds for public services.",
        "isCorrect": false
      },
      {
        "text": "Escheat",
        "rationale": "Escheat is the reversion of property to the state when an owner dies without a will and without heirs.",
        "isCorrect": false
      }
    ],
    "hint": "This is one of the four basic powers of government over real property."
  },
  {
    "questionNumber": 81,
    "question": "A buyer is purchasing a home and wants to have the property inspected. The buyer should:",
    "answerOptions": [
      {
        "text": "Hire a licensed and qualified home inspector.",
        "rationale": "A home inspection is an important part of the due diligence process, and the buyer should hire a professional to perform the inspection.",
        "isCorrect": true
      },
      {
        "text": "Ask the seller to perform the inspection.",
        "rationale": "The seller has a conflict of interest and should not be the one to perform the inspection.",
        "isCorrect": false
      },
      {
        "text": "Rely on the appraisal.",
        "rationale": "An appraisal is an estimate of value, not a home inspection.",
        "isCorrect": false
      },
      {
        "text": "Inspect the property themselves.",
        "rationale": "While the buyer should visually inspect the property, they are not a substitute for a professional home inspector.",
        "isCorrect": false
      }
    ],
    "hint": "A home inspection is a critical step in the home buying process, and it should be done by an expert."
  },
  {
    "questionNumber": 82,
    "question": "A title insurance policy protects the insured against:",
    "answerOptions": [
      {
        "text": "Physical damage to the property.",
        "rationale": "Homeowners insurance protects against physical damage to the property.",
        "isCorrect": false
      },
      {
        "text": "Defects in the title to the property that were not discovered during the title search.",
        "rationale": "Title insurance is a form of indemnity insurance that protects the insured from financial loss due to defects in the title.",
        "isCorrect": true
      },
      {
        "text": "A decrease in the value of the property.",
        "rationale": "Title insurance does not protect against market fluctuations.",
        "isCorrect": false
      },
      {
        "text": "The borrower defaulting on the loan.",
        "rationale": "Mortgage insurance protects the lender against default.",
        "isCorrect": false
      }
    ],
    "hint": "This type of insurance is all about protecting the owner's legal rights to the property."
  },
  {
    "questionNumber": 83,
    "question": "In Georgia, a real estate salesperson can only receive a commission from:",
    "answerOptions": [
      {
        "text": "The seller",
        "rationale": "A salesperson can only be paid by their sponsoring broker.",
        "isCorrect": false
      },
      {
        "text": "The buyer",
        "rationale": "A salesperson can only be paid by their sponsoring broker.",
        "isCorrect": false
      },
      {
        "text": "Their sponsoring broker",
        "rationale": "All commissions must be paid through the salesperson's sponsoring broker.",
        "isCorrect": true
      },
      {
        "text": "Any licensed broker",
        "rationale": "A salesperson can only be paid by the broker with whom their license is affiliated.",
        "isCorrect": false
      }
    ],
    "hint": "There is a specific chain of payment for real estate commissions in Georgia."
  },
  {
    "questionNumber": 84,
    "question": "A contract that is created by the actions of the parties, rather than by their express words, is known as a(n):",
    "answerOptions": [
      {
        "text": "Express contract",
        "rationale": "An express contract is one in which the terms are stated in words, either written or oral.",
        "isCorrect": false
      },
      {
        "text": "Implied contract",
        "rationale": "An implied contract is one that is created by the conduct of the parties.",
        "isCorrect": true
      },
      {
        "text": "Bilateral contract",
        "rationale": "A bilateral contract is one in which both parties make a promise.",
        "isCorrect": false
      },
      {
        "text": "Unilateral contract",
        "rationale": "A unilateral contract is one in which one party makes a promise in exchange for an act.",
        "isCorrect": false
      }
    ],
    "hint": "This type of contract is not written down, but it is still legally binding."
  },
  {
    "questionNumber": 85,
    "question": "A homeowner has a mortgage with a prepayment penalty clause.  This means that:",
    "answerOptions": [
      {
        "text": "The homeowner will be charged a fee if they pay off the loan early.",
        "rationale": "A prepayment penalty clause allows the lender to charge a fee if the borrower pays off the loan before the end of the term.",
        "isCorrect": true
      },
      {
        "text": "The homeowner can pay off the loan at any time without a penalty.",
        "rationale": "This would be the case if the loan did not have a prepayment penalty clause.",
        "isCorrect": false
      },
      {
        "text": "The homeowner must make extra payments on the loan.",
        "rationale": "This is not what a prepayment penalty clause is.",
        "isCorrect": false
      },
      {
        "text": "The interest rate on the loan will increase if the homeowner makes extra payments.",
        "rationale": "This is not what a prepayment penalty clause is.",
        "isCorrect": false
      }
    ],
    "hint": "This clause in a loan agreement can make it more expensive to refinance or sell a property."
  },
  {
    "questionNumber": 86,
    "question": "A buyer is purchasing a property and has a 10-day due diligence period.  During this time, the buyer can:",
    "answerOptions": [
      {
        "text": "Terminate the contract for any reason or no reason at all.",
        "rationale": "The due diligence period gives the buyer the right to investigate the property and terminate the contract if they are not satisfied with their findings.",
        "isCorrect": true
      },
      {
        "text": "Only terminate the contract if the property has a major defect.",
        "rationale": "The due diligence period gives the buyer a broad right to terminate the contract.",
        "isCorrect": false
      },
      {
        "text": "Not terminate the contract, but can ask the seller to make repairs.",
        "rationale": "The buyer has the right to terminate the contract during the due diligence period.",
        "isCorrect": false
      },
      {
        "text": "Only terminate the contract if they are unable to get financing.",
        "rationale": "This would be a financing contingency, not a due diligence period.",
        "isCorrect": false
      }
    ],
    "hint": "The due diligence period is a critical time for the buyer to do their homework on the property."
  },
  {
    "questionNumber": 87,
    "question": "The fiduciary duty of confidentiality requires a real estate agent to:",
    "answerOptions": [
      {
        "text": "Keep all information about the client confidential, even after the agency relationship has ended.",
        "rationale": "The duty of confidentiality survives the termination of the agency relationship.",
        "isCorrect": true
      },
      {
        "text": "Only keep financial information about the client confidential.",
        "rationale": "The duty of confidentiality extends to all information that the agent learns about the client during the course of the agency relationship.",
        "isCorrect": false
      },
      {
        "text": "Share confidential information with other agents in their office.",
        "rationale": "This would be a breach of the duty of confidentiality.",
        "isCorrect": false
      },
      {
        "text": "Disclose confidential information if it will help them get a higher commission.",
        "rationale": "This would be a breach of the agent's fiduciary duty to the client.",
        "isCorrect": false
      }
    ],
    "hint": "This fiduciary duty is about protecting the client's privacy."
  },
  {
    "questionNumber": 88,
    "question": "A property is zoned for single-family residential use.  A person wants to open a small business on the property.  They would need to obtain a:",
    "answerOptions": [
      {
        "text": "Building permit",
        "rationale": "A building permit is required for construction, but it does not change the zoning of the property.",
        "isCorrect": false
      },
      {
        "text": "Variance",
        "rationale": "A variance is a request to deviate from the current zoning requirements. If granted, it would permit a use of the land that is otherwise prohibited by the zoning ordinance.",
        "isCorrect": true
      },
      {
        "text": "Nonconforming use permit",
        "rationale": "A nonconforming use is a use that was legal before the zoning ordinance was enacted but is no longer permitted.",
        "isCorrect": false
      },
      {
        "text": "Certificate of occupancy",
        "rationale": "A certificate of occupancy is issued after a building has been inspected and found to be in compliance with the building code.",
        "isCorrect": false
      }
    ],
    "hint": "This is a form of relief from the strict application of a zoning ordinance."
  },
  {
    "questionNumber": 89,
    "question": "A property is sold and the closing takes place on August 10th. The seller has already paid the annual homeowners' association dues of $600. How will the dues be prorated at closing?",
    "answerOptions": [
      {
        "text": "The buyer will owe the seller $200.",
        "rationale": "The seller is responsible for the dues from January 1st to August 9th. The buyer is responsible from August 10th to December 31st. $600 / 12 months = $50/month. The buyer will owe the seller for approximately 4.67 months of dues, or about $233.50. None of the options are exactly correct, but this is a common type of proration question.",
        "isCorrect": false
      },
      {
        "text": "The seller will owe the buyer $200.",
        "rationale": "This is not the correct calculation.",
        "isCorrect": false
      },
      {
        "text": "The buyer will owe the seller $400.",
        "rationale": "This is not the correct calculation.",
        "isCorrect": false
      },
      {
        "text": "The seller will owe the buyer $400.",
        "rationale": "This is not the correct calculation.",
        "isCorrect": false
      }
    ],
    "hint": "Proration is the process of dividing expenses between the buyer and the seller at closing."
  },
  {
    "questionNumber": 90,
    "question": "The substitution of a new contract for an old one, or the substitution of a new party for an old one, is known as:",
    "answerOptions": [
      {
        "text": "Assignment",
        "rationale": "An assignment is the transfer of rights or duties under a contract.",
        "isCorrect": false
      },
      {
        "text": "Novation",
        "rationale": "Novation is the substitution of a new contract for an existing one, or the substitution of a new party for an existing one. It requires the consent of all parties.",
        "isCorrect": true
      },
      {
        "text": "Rescission",
        "rationale": "Rescission is the cancellation of a contract.",
        "isCorrect": false
      },
      {
        "text": "Reformation",
        "rationale": "Reformation is the correction of a contract to reflect the true intention of the parties.",
        "isCorrect": false
      }
    ],
    "hint": "This is a way to change a contract by creating a new one."
  },
  {
    "questionNumber": 91,
    "question": "A landlord has a 'no pets' policy.  A prospective tenant has a disability and needs a service animal.  The landlord must:",
    "answerOptions": [
      {
        "text": "Allow the tenant to have the service animal, as it is a reasonable accommodation.",
        "rationale": "The Fair Housing Act requires landlords to make reasonable accommodations for people with disabilities, and this includes allowing service animals even in properties with a 'no pets' policy.",
        "isCorrect": true
      },
      {
        "text": "Not allow the tenant to have the service animal, as it would violate the 'no pets' policy.",
        "rationale": "The Fair Housing Act overrides the 'no pets' policy in this case.",
        "isCorrect": false
      },
      {
        "text": "Charge the tenant a pet deposit for the service animal.",
        "rationale": "A landlord cannot charge a pet deposit for a service animal.",
        "isCorrect": false
      },
      {
        "text": "Ask the tenant for proof of the disability.",
        "rationale": "A landlord can ask for verification that the tenant has a disability and needs the service animal, but they cannot ask for details about the disability.",
        "isCorrect": false
      }
    ],
    "hint": "The Fair Housing Act has specific rules about how landlords must accommodate tenants with disabilities."
  },
  {
    "questionNumber": 92,
    "question": "The Sherman Antitrust Act prohibits which of the following?",
    "answerOptions": [
      {
        "text": "Price fixing",
        "rationale": "Price fixing is an agreement between competitors to set prices at a certain level, and it is a violation of antitrust laws.",
        "isCorrect": false
      },
      {
        "text": "Group boycotting",
        "rationale": "Group boycotting is an agreement between competitors to not do business with a particular person or company, and it is a violation of antitrust laws.",
        "isCorrect": false
      },
      {
        "text": "Allocation of markets",
        "rationale": "Allocation of markets is an agreement between competitors to divide up a market, and it is a violation of antitrust laws.",
        "isCorrect": false
      },
      {
        "text": "All of the above.",
        "rationale": "The Sherman Antitrust Act prohibits all of these anticompetitive practices.",
        "isCorrect": true
      }
    ],
    "hint": "This federal law is designed to promote competition in the marketplace."
  },
  {
    "questionNumber": 93,
    "question": "A buyer is purchasing a property and the seller has agreed to pay for a one-year home warranty.  The home warranty will cover:",
    "answerOptions": [
      {
        "text": "The structure of the home.",
        "rationale": "A home warranty is a service contract that covers the repair or replacement of major home systems and appliances.",
        "isCorrect": false
      },
      {
        "text": "The personal property of the buyer.",
        "rationale": "A home warranty does not cover personal property.",
        "isCorrect": false
      },
      {
        "text": "The major systems and appliances in the home.",
        "rationale": "A home warranty typically covers things like the plumbing, electrical, heating, and air conditioning systems, as well as major appliances like the refrigerator, stove, and dishwasher.",
        "isCorrect": true
      },
      {
        "text": "Any defects that are discovered during the home inspection.",
        "rationale": "A home warranty is a service contract, not a guarantee that the home is free of defects.",
        "isCorrect": false
      }
    ],
    "hint": "A home warranty is a way to protect the buyer from unexpected repair costs after they move in."
  },
  {
    "questionNumber": 94,
    "question": "The fiduciary duty of accounting requires a real estate agent to:",
    "answerOptions": [
      {
        "text": "Account for all money and property that they receive on behalf of their client.",
        "rationale": "The duty of accounting requires the agent to keep accurate records of all funds and property that they handle for their client.",
        "isCorrect": true
      },
      {
        "text": "Provide their client with a monthly financial statement.",
        "rationale": "While an agent must be able to account for all funds, a monthly financial statement is not a specific requirement.",
        "isCorrect": false
      },
      {
        "text": "Give their client financial advice.",
        "rationale": "A real estate agent is not a financial advisor and should not give financial advice.",
        "isCorrect": false
      },
      {
        "text": "Pay for any expenses that are incurred in the transaction.",
        "rationale": "The agent is not responsible for paying for the client's expenses.",
        "isCorrect": false
      }
    ],
    "hint": "This fiduciary duty is about being responsible for the client's money and property."
  },
  {
    "questionNumber": 95,
    "question": "A salesperson is working for a broker and decides to leave the brokerage.  The salesperson's listings:",
    "answerOptions": [
      {
        "text": "Belong to the salesperson and can be taken with them to their new brokerage.",
        "rationale": "Listing agreements are with the broker, not the salesperson.",
        "isCorrect": false
      },
      {
        "text": "Belong to the broker and remain with the brokerage.",
        "rationale": "The listing agreement is a contract between the seller and the broker, so the listings stay with the broker when the salesperson leaves.",
        "isCorrect": true
      },
      {
        "text": "Are automatically terminated.",
        "rationale": "The listings are not terminated when the salesperson leaves.",
        "isCorrect": false
      },
      {
        "text": "Are transferred to the new broker.",
        "rationale": "The listings can only be transferred to the new broker with the seller's consent.",
        "isCorrect": false
      }
    ],
    "hint": "The listing agreement is a contract between the seller and the broker, not the salesperson."
  },
  {
    "questionNumber": 96,
    "question": "A buyer is purchasing a property and the lender requires an appraisal.  The appraiser will:",
    "answerOptions": [
      {
        "text": "Estimate the value of the property.",
        "rationale": "An appraisal is a professional opinion of a property's market value.",
        "isCorrect": true
      },
      {
        "text": "Inspect the property for defects.",
        "rationale": "A home inspection is a separate process from an appraisal.",
        "isCorrect": false
      },
      {
        "text": "Determine the loan amount.",
        "rationale": "The lender determines the loan amount based on the appraisal, but the appraiser does not make that decision.",
        "isCorrect": false
      },
      {
        "text": "Negotiate the sale price.",
        "rationale": "The sale price is negotiated between the buyer and the seller.",
        "isCorrect": false
      }
    ],
    "hint": "An appraisal is an important part of the lending process, and it is used to determine the property's value."
  },
  {
    "questionNumber": 97,
    "question": "A property is sold for $200,000.  The commission is 6% and is split equally between the listing broker and the selling broker.  The selling salesperson has a 50/50 split with their broker.  How much will the selling salesperson receive?",
    "answerOptions": [
      {
        "text": "$3,000",
        "rationale": "Total commission = $200,000 * 0.06 = $12,000. Selling broker's share = $12,000 / 2 = $6,000. Selling salesperson's share = $6,000 * 0.50 = $3,000.",
        "isCorrect": true
      },
      {
        "text": "$6,000",
        "rationale": "This is the selling broker's share, not the salesperson's share.",
        "isCorrect": false
      },
      {
        "text": "$12,000",
        "rationale": "This is the total commission.",
        "isCorrect": false
      },
      {
        "text": "$1,500",
        "rationale": "This amount is incorrect.",
        "isCorrect": false
      }
    ],
    "hint": "There are several steps to this calculation, so be sure to follow them in order."
  },
  {
    "questionNumber": 98,
    "question": "A person who has a life estate in a property can do all of the following EXCEPT:",
    "answerOptions": [
      {
        "text": "Sell their life estate.",
        "rationale": "A life tenant can sell their interest in the property, but the life estate will terminate upon their death.",
        "isCorrect": false
      },
      {
        "text": "Lease the property.",
        "rationale": "A life tenant can lease the property, but the lease will terminate upon their death.",
        "isCorrect": false
      },
      {
        "text": "Will the property to their heirs.",
        "rationale": "A life estate is not an estate of inheritance, so the life tenant cannot will the property to their heirs.",
        "isCorrect": true
      },
      {
        "text": "Mortgage the property.",
        "rationale": "A life tenant can mortgage their interest in the property.",
        "isCorrect": false
      }
    ],
    "hint": "A life estate is a form of ownership that is limited to the lifetime of a specific person."
  },
  {
    "questionNumber": 99,
    "question": "A buyer is purchasing a property in a condominium complex. The buyer will own:",
    "answerOptions": [
      {
        "text": "A share in the corporation that owns the building.",
        "rationale": "This is a characteristic of a cooperative, not a condominium.",
        "isCorrect": false
      },
      {
        "text": "The land under their unit.",
        "rationale": "In a condominium, the land is owned in common by all of the unit owners.",
        "isCorrect": false
      },
      {
        "text": "The air space within their unit and an undivided interest in the common elements.",
        "rationale": "This is the definition of condominium ownership.",
        "isCorrect": true
      },
      {
        "text": "A leasehold interest in their unit.",
        "rationale": "A condominium owner has a freehold interest in their unit.",
        "isCorrect": false
      }
    ],
    "hint": "Condominium ownership is a unique form of ownership that has two components."
  },
  {
    "questionNumber": 100,
    "question": "The Federal National Mortgage Association (Fannie Mae) is a:",
    "answerOptions": [
      {
        "text": "Primary mortgage market lender.",
        "rationale": "Fannie Mae is a secondary mortgage market entity.",
        "isCorrect": false
      },
      {
        "text": "Government agency that insures loans.",
        "rationale": "Fannie Mae is a government-sponsored enterprise that buys and sells mortgages, but it does not insure them.",
        "isCorrect": false
      },
      {
        "text": "Secondary mortgage market entity that buys and sells mortgages.",
        "rationale": "Fannie Mae, along with Freddie Mac and Ginnie Mae, is a major player in the secondary mortgage market.",
        "isCorrect": true
      },
      {
        "text": "Private company that originates loans.",
        "rationale": "Fannie Mae does not originate loans.",
        "isCorrect": false
      }
    ],
    "hint": "This organization plays a key role in the housing finance system by providing liquidity to the mortgage market."
  },
  {
    "questionNumber": 101,
    "question": "A buyer is purchasing a property and the seller has agreed to leave the washer and dryer. This should be stated in the:",
    "answerOptions": [
      {
        "text": "Deed",
        "rationale": "The deed transfers title to the real property, not the personal property.",
        "isCorrect": false
      },
      {
        "text": "Mortgage",
        "rationale": "The mortgage is the loan agreement.",
        "isCorrect": false
      },
      {
        "text": "Purchase and sale agreement",
        "rationale": "The purchase and sale agreement should specify all of the terms of the sale, including any personal property that is being transferred.",
        "isCorrect": true
      },
      {
        "text": "Title insurance policy",
        "rationale": "The title insurance policy protects the buyer's title to the real property.",
        "isCorrect": false
      }
    ],
    "hint": "The purchase and sale agreement is the main contract that governs the transaction, and it should include all of the details."
  },
  {
    "questionNumber": 102,
    "question": "A property is sold and the closing takes place. The buyer's agent should:",
    "answerOptions": [
      {
        "text": "Give the buyer the keys to the property.",
        "rationale": "The keys are typically given to the buyer at closing.",
        "isCorrect": true
      },
      {
        "text": "Change the locks on the property.",
        "rationale": "The buyer is responsible for changing the locks.",
        "isCorrect": false
      },
      {
        "text": "Help the buyer move in.",
        "rationale": "This is not part of the agent's duties.",
        "isCorrect": false
      },
      {
        "text": "Do a final walk-through of the property.",
        "rationale": "The final walk-through should be done before closing.",
        "isCorrect": false
      }
    ],
    "hint": "At closing, the final steps of the transaction are completed."
  },
  {
    "questionNumber": 103,
    "question": "A property is being sold 'as is.' This means that:",
    "answerOptions": [
      {
        "text": "The seller is not required to disclose any known defects.",
        "rationale": "The seller is still required to disclose known material defects.",
        "isCorrect": false
      },
      {
        "text": "The buyer is not allowed to have the property inspected.",
        "rationale": "The buyer should always have the property inspected, even if it is being sold 'as is.'",
        "isCorrect": false
      },
      {
        "text": "The seller will not make any repairs to the property.",
        "rationale": "'As is' means that the seller is selling the property in its current condition and will not make any repairs.",
        "isCorrect": true
      },
      {
        "text": "The property is in perfect condition.",
        "rationale": "'As is' does not mean that the property is in perfect condition.",
        "isCorrect": false
      }
    ],
    "hint": "The term 'as is' has a specific meaning in a real estate transaction."
  },
  {
    "questionNumber": 104,
    "question": "A person who is authorized to act on behalf of another person is known as a(n):",
    "answerOptions": [
      {
        "text": "Principal",
        "rationale": "The principal is the person who hires the agent.",
        "isCorrect": false
      },
      {
        "text": "Agent",
        "rationale": "An agent is a person who is authorized to act on behalf of another person, known as the principal.",
        "isCorrect": true
      },
      {
        "text": "Client",
        "rationale": "A client is a person who has an agency relationship with an agent.",
        "isCorrect": false
      },
      {
        "text": "Customer",
        "rationale": "A customer is a person who does not have an agency relationship with an agent.",
        "isCorrect": false
      }
    ],
    "hint": "This is a key term in the law of agency."
  },
  {
    "questionNumber": 105,
    "question": "A buyer is purchasing a property and is concerned about the condition of the roof. The buyer should:",
    "answerOptions": [
      {
        "text": "Have a roofer inspect the roof.",
        "rationale": "If the buyer has a specific concern about a part of the property, they should have an expert inspect it.",
        "isCorrect": true
      },
      {
        "text": "Ask the seller if the roof leaks.",
        "rationale": "While the buyer can ask the seller, they should not rely solely on the seller's representations.",
        "isCorrect": false
      },
      {
        "text": "Look at the roof from the ground.",
        "rationale": "A visual inspection from the ground is not sufficient to determine the condition of the roof.",
        "isCorrect": false
      },
      {
        "text": "Assume that the roof is in good condition.",
        "rationale": "The buyer should not make assumptions about the condition of the property.",
        "isCorrect": false
      }
    ],
    "hint": "A buyer should take proactive steps to investigate any concerns they have about a property."
  },
  {
    "questionNumber": 106,
    "question": "A property is located in a 100-year floodplain. This means that:",
    "answerOptions": [
      {
        "text": "The property will flood every 100 years.",
        "rationale": "A 100-year floodplain is an area that has a 1% chance of flooding in any given year.",
        "isCorrect": false
      },
      {
        "text": "The property has a 1% chance of flooding in any given year.",
        "rationale": "This is the definition of a 100-year floodplain.",
        "isCorrect": true
      },
      {
        "text": "The property has flooded in the past 100 years.",
        "rationale": "This is not necessarily true.",
        "isCorrect": false
      },
      {
        "text": "The property is not at risk of flooding.",
        "rationale": "The property is at risk of flooding.",
        "isCorrect": false
      }
    ],
    "hint": "The term '100-year floodplain' is a statistical term that can be misleading."
  },
  {
    "questionNumber": 107,
    "question": "A buyer is purchasing a property and the lender requires a survey. The survey will:",
    "answerOptions": [
      {
        "text": "Determine the value of the property.",
        "rationale": "An appraisal determines the value of the property.",
        "isCorrect": false
      },
      {
        "text": "Identify any encroachments or boundary line issues.",
        "rationale": "A survey is a measurement of a property's boundaries and can reveal any problems with the property lines.",
        "isCorrect": true
      },
      {
        "text": "Inspect the property for defects.",
        "rationale": "A home inspection is a separate process from a survey.",
        "isCorrect": false
      },
      {
        "text": "Provide a history of the property's ownership.",
        "rationale": "A title search provides a history of the property's ownership.",
        "isCorrect": false
      }
    ],
    "hint": "A survey is a way to make sure that the buyer is getting what they are paying for."
  },
  {
    "questionNumber": 108,
    "question": "A seller has accepted an offer on their property, but they have also received a backup offer. This means that:",
    "answerOptions": [
      {
        "text": "The seller can terminate the first contract and accept the backup offer.",
        "rationale": "The seller is bound by the first contract unless it is terminated for a valid reason.",
        "isCorrect": false
      },
      {
        "text": "The backup offer will become the primary contract if the first contract is terminated.",
        "rationale": "A backup offer is a contract that is contingent on the termination of the first contract.",
        "isCorrect": true
      },
      {
        "text": "The seller can accept both offers.",
        "rationale": "The seller can only accept one offer at a time.",
        "isCorrect": false
      },
      {
        "text": "The backup offer is not a valid contract.",
        "rationale": "A backup offer is a valid contract, but it is in a secondary position.",
        "isCorrect": false
      }
    ],
    "hint": "A backup offer is a way for a seller to have a second buyer lined up in case the first one falls through."
  },
  {
    "questionNumber": 109,
    "question": "A buyer is purchasing a property and is getting a VA loan. This means that:",
    "answerOptions": [
      {
        "text": "The buyer is a veteran or active-duty service member.",
        "rationale": "VA loans are available to eligible veterans, active-duty service members, and some surviving spouses.",
        "isCorrect": false
      },
      {
        "text": "The buyer is not required to make a down payment.",
        "rationale": "VA loans often do not require a down payment.",
        "isCorrect": false
      },
      {
        "text": "The loan is guaranteed by the U.S. Department of Veterans Affairs.",
        "rationale": "The VA guarantees a portion of the loan, which protects the lender in case of default.",
        "isCorrect": false
      },
      {
        "text": "All of the above.",
        "rationale": "All of these statements are true about VA loans.",
        "isCorrect": true
      }
    ],
    "hint": "VA loans are a special type of loan that is available to those who have served in the military."
  },
  {
    "questionNumber": 110,
    "question": "A real estate agent is showing a property to a buyer and notices a water stain on the ceiling. The agent should:",
    "answerOptions": [
      {
        "text": "Ignore it, as it is not their responsibility.",
        "rationale": "The agent has a duty to disclose any known material facts about the property.",
        "isCorrect": false
      },
      {
        "text": "Point it out to the buyer and recommend that they have it investigated.",
        "rationale": "The agent should disclose the water stain and advise the buyer to have it checked out by a professional.",
        "isCorrect": true
      },
      {
        "text": "Tell the buyer that it is an old stain and not a problem.",
        "rationale": "The agent should not make any representations about the condition of the property that they do not know to be true.",
        "isCorrect": false
      },
      {
        "text": "Cover it up with paint.",
        "rationale": "This would be a fraudulent misrepresentation.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent has a duty to be honest and fair with all parties in a transaction."
  },
  {
    "questionNumber": 111,
    "question": "A property is sold at a foreclosure sale. The new owner receives a:",
    "answerOptions": [
      {
        "text": "General warranty deed",
        "rationale": "A general warranty deed provides the most protection to the grantee, but it is not typically used in a foreclosure sale.",
        "isCorrect": false
      },
      {
        "text": "Special warranty deed",
        "rationale": "A special warranty deed is sometimes used in a foreclosure sale, but a sheriff's deed is more common.",
        "isCorrect": false
      },
      {
        "text": "Sheriff's deed",
        "rationale": "A sheriff's deed is a type of deed that is given to the purchaser of a property at a foreclosure sale.",
        "isCorrect": true
      },
      {
        "text": "Quitclaim deed",
        "rationale": "A quitclaim deed offers the least protection and is not typically used in a foreclosure sale.",
        "isCorrect": false
      }
    ],
    "hint": "The type of deed that is used in a foreclosure sale is different from the type of deed that is used in a traditional sale."
  },
  {
    "questionNumber": 112,
    "question": "A buyer is purchasing a property and the seller has agreed to pay some of the buyer's closing costs. This is known as:",
    "answerOptions": [
      {
        "text": "Seller financing",
        "rationale": "Seller financing is when the seller provides the loan to the buyer.",
        "isCorrect": false
      },
      {
        "text": "A seller concession",
        "rationale": "A seller concession is when the seller agrees to pay some of the buyer's closing costs.",
        "isCorrect": true
      },
      {
        "text": "A gift of equity",
        "rationale": "A gift of equity is when a seller gives a buyer a portion of the equity in the property.",
        "isCorrect": false
      },
      {
        "text": "A reverse mortgage",
        "rationale": "A reverse mortgage is a loan for seniors that allows them to convert the equity in their home into cash.",
        "isCorrect": false
      }
    ],
    "hint": "This is a way for a seller to help a buyer with the costs of purchasing a home."
  },
  {
    "questionNumber": 113,
    "question": "A real estate agent is working with a seller who wants to discriminate against a protected class. The agent should:",
    "answerOptions": [
      {
        "text": "Refuse to take the listing.",
        "rationale": "An agent must not participate in any discriminatory practices.",
        "isCorrect": true
      },
      {
        "text": "Take the listing and hope that no one finds out.",
        "rationale": "This would be a violation of fair housing laws.",
        "isCorrect": false
      },
      {
        "text": "Report the seller to the Georgia Real Estate Commission.",
        "rationale": "While the agent should not participate in the discrimination, they are not required to report the seller to the Commission.",
        "isCorrect": false
      },
      {
        "text": "Refer the seller to another agent.",
        "rationale": "This would be a violation of fair housing laws.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent has a duty to uphold the fair housing laws."
  },
  {
    "questionNumber": 114,
    "question": "A property is held in a trust. The person who manages the trust is known as the:",
    "answerOptions": [
      {
        "text": "Trustor",
        "rationale": "The trustor is the person who creates the trust.",
        "isCorrect": false
      },
      {
        "text": "Trustee",
        "rationale": "The trustee is the person who holds legal title to the property and is responsible for managing the trust.",
        "isCorrect": true
      },
      {
        "text": "Beneficiary",
        "rationale": "The beneficiary is the person who benefits from the trust.",
        "isCorrect": false
      },
      {
        "text": "Grantor",
        "rationale": "The grantor is another name for the trustor.",
        "isCorrect": false
      }
    ],
    "hint": "A trust is a legal arrangement in which a person holds property for the benefit of another."
  },
  {
    "questionNumber": 115,
    "question": "A buyer is purchasing a property and the closing is scheduled for a few weeks. The buyer's agent should:",
    "answerOptions": [
      {
        "text": "Stay in contact with the buyer and the lender to make sure everything is on track.",
        "rationale": "The agent should continue to monitor the progress of the transaction and keep the buyer informed.",
        "isCorrect": true
      },
      {
        "text": "Assume that everything is fine and not contact the buyer until closing.",
        "rationale": "The agent should be proactive and stay on top of the transaction.",
        "isCorrect": false
      },
      {
        "text": "Tell the buyer to contact the lender directly with any questions.",
        "rationale": "The agent should be the buyer's primary point of contact.",
        "isCorrect": false
      },
      {
        "text": "Start looking for a new property for the buyer.",
        "rationale": "The agent should focus on getting the current transaction to closing.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent's job is not over when the contract is signed."
  },
  {
    "questionNumber": 116,
    "question": "A property has an underground storage tank that is leaking. This is a potential environmental hazard that must be:",
    "answerOptions": [
      {
        "text": "Disclosed to the buyer.",
        "rationale": "A leaking underground storage tank is a material fact that must be disclosed to the buyer.",
        "isCorrect": true
      },
      {
        "text": "Ignored",
        "rationale": "This is a serious environmental issue that cannot be ignored.",
        "isCorrect": false
      },
      {
        "text": "Reported to the EPA.",
        "rationale": "Depending on the circumstances, the leak may need to be reported to the EPA, but the seller's primary responsibility is to disclose it to the buyer.",
        "isCorrect": false
      },
      {
        "text": "Cleaned up by the buyer.",
        "rationale": "The seller is responsible for cleaning up the leak.",
        "isCorrect": false
      }
    ],
    "hint": "A seller has a duty to disclose known material defects to the buyer."
  },
  {
    "questionNumber": 117,
    "question": "A real estate agent is working with a buyer who is from another country and does not speak English well. The agent should:",
    "answerOptions": [
      {
        "text": "Recommend that the buyer get a translator.",
        "rationale": "It is important that the buyer understands all of the documents they are signing, and a translator can help with that.",
        "isCorrect": true
      },
      {
        "text": "Try to explain the documents to the buyer in English.",
        "rationale": "This could lead to misunderstandings and legal problems.",
        "isCorrect": false
      },
      {
        "text": "Have the buyer sign the documents without reading them.",
        "rationale": "This would be a breach of the agent's fiduciary duty to the client.",
        "isCorrect": false
      },
      {
        "text": "Refuse to work with the buyer.",
        "rationale": "This could be a violation of fair housing laws.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent has a duty to make sure that their client understands the transaction."
  },
  {
    "questionNumber": 118,
    "question": "A property is sold and the seller is a foreign person. The buyer may be required to withhold a portion of the sale price and send it to the IRS under the:",
    "answerOptions": [
      {
        "text": "Real Estate Settlement Procedures Act (RESPA)",
        "rationale": "RESPA deals with disclosures of closing costs.",
        "isCorrect": false
      },
      {
        "text": "Foreign Investment in Real Property Tax Act (FIRPTA)",
        "rationale": "FIRPTA requires that a portion of the proceeds from the sale of real property by a foreign person be withheld to ensure that the seller pays any required taxes.",
        "isCorrect": true
      },
      {
        "text": "Truth in Lending Act (TILA)",
        "rationale": "TILA deals with disclosures of the cost of credit.",
        "isCorrect": false
      },
      {
        "text": "Fair Housing Act",
        "rationale": "The Fair Housing Act prohibits discrimination in housing.",
        "isCorrect": false
      }
    ],
    "hint": "This federal law is designed to make sure that foreign sellers pay their U.S. taxes."
  },
  {
    "questionNumber": 119,
    "question": "A buyer is purchasing a property and the seller has agreed to make some repairs before closing. The buyer's agent should:",
    "answerOptions": [
      {
        "text": "Make sure that the repairs are done to the buyer's satisfaction.",
        "rationale": "The agent should follow up to make sure that the seller has completed the repairs as agreed.",
        "isCorrect": true
      },
      {
        "text": "Assume that the seller will make the repairs.",
        "rationale": "The agent should not make assumptions.",
        "isCorrect": false
      },
      {
        "text": "Tell the buyer to make the repairs themselves.",
        "rationale": "The seller has agreed to make the repairs.",
        "isCorrect": false
      },
      {
        "text": "Cancel the contract.",
        "rationale": "This would not be appropriate unless the seller refuses to make the repairs.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent's job is not over when the contract is signed."
  },
  {
    "questionNumber": 120,
    "question": "A property is sold and the closing takes place. The seller's agent should:",
    "answerOptions": [
      {
        "text": "Give the seller their proceeds from the sale.",
        "rationale": "The closing attorney is responsible for disbursing the funds.",
        "isCorrect": false
      },
      {
        "text": "Make sure that the seller has a new place to live.",
        "rationale": "This is not part of the agent's duties.",
        "isCorrect": false
      },
      {
        "text": "Keep in touch with the seller after closing.",
        "rationale": "Good customer service includes following up with clients after the transaction is complete.",
        "isCorrect": true
      },
      {
        "text": "Do a final walk-through of the property.",
        "rationale": "The final walk-through is for the buyer, not the seller.",
        "isCorrect": false
      }
    ],
    "hint": "Good customer service can lead to future business."
  },
  {
    "questionNumber": 121,
    "question": "A buyer is purchasing a property and the seller has provided a property condition disclosure statement. The buyer should:",
    "answerOptions": [
      {
        "text": "Rely on the disclosure statement and not have the property inspected.",
        "rationale": "The buyer should always have the property inspected, even if the seller provides a disclosure statement.",
        "isCorrect": false
      },
      {
        "text": "Use the disclosure statement as a starting point for their own investigation.",
        "rationale": "The disclosure statement is a good source of information, but the buyer should still do their own due diligence.",
        "isCorrect": true
      },
      {
        "text": "Ignore the disclosure statement.",
        "rationale": "The disclosure statement contains important information about the property.",
        "isCorrect": false
      },
      {
        "text": "Sue the seller if they find a defect that was not disclosed.",
        "rationale": "While the buyer may have legal recourse if the seller intentionally concealed a defect, their first step should be to have the property inspected.",
        "isCorrect": false
      }
    ],
    "hint": "A property condition disclosure statement is a good source of information, but it is not a substitute for a home inspection."
  },
  {
    "questionNumber": 122,
    "question": "A real estate agent is preparing a comparative market analysis (CMA) for a seller. The agent should:",
    "answerOptions": [
      {
        "text": "Only use properties that have sold in the past month.",
        "rationale": "The agent should use the most recent comparable sales available, but they may need to go back further than one month.",
        "isCorrect": false
      },
      {
        "text": "Use properties that are similar to the seller's property in size, location, and condition.",
        "rationale": "The key to a good CMA is to use comparable properties.",
        "isCorrect": true
      },
      {
        "text": "Use the most expensive properties in the neighborhood.",
        "rationale": "This would not be an accurate CMA.",
        "isCorrect": false
      },
      {
        "text": "Use the least expensive properties in the neighborhood.",
        "rationale": "This would not be an accurate CMA.",
        "isCorrect": false
      }
    ],
    "hint": "A CMA is a tool that is used to help a seller determine a listing price for their property."
  },
  {
    "questionNumber": 123,
    "question": "A buyer is purchasing a property and is getting an FHA loan. This means that:",
    "answerOptions": [
      {
        "text": "The loan is insured by the Federal Housing Administration.",
        "rationale": "FHA loans are insured by the FHA, which protects the lender in case of default.",
        "isCorrect": true
      },
      {
        "text": "The buyer is a first-time homebuyer.",
        "rationale": "While FHA loans are popular with first-time homebuyers, they are not limited to them.",
        "isCorrect": false
      },
      {
        "text": "The buyer is not required to make a down payment.",
        "rationale": "FHA loans require a down payment, although it is typically smaller than for a conventional loan.",
        "isCorrect": false
      },
      {
        "text": "The interest rate on the loan is lower than for a conventional loan.",
        "rationale": "The interest rate on an FHA loan is not necessarily lower than for a conventional loan.",
        "isCorrect": false
      }
    ],
    "hint": "FHA loans are a type of government-insured loan that is designed to make homeownership more accessible."
  },
  {
    "questionNumber": 124,
    "question": "A real estate agent is working with a buyer who wants to make a lowball offer on a property. The agent should:",
    "answerOptions": [
      {
        "text": "Refuse to write the offer.",
        "rationale": "The agent has a duty to present all offers to the seller.",
        "isCorrect": false
      },
      {
        "text": "Write the offer and present it to the seller.",
        "rationale": "The agent must present all offers, regardless of their opinion of the price.",
        "isCorrect": true
      },
      {
        "text": "Tell the buyer that the seller will not accept the offer.",
        "rationale": "The agent should not make any predictions about how the seller will respond to an offer.",
        "isCorrect": false
      },
      {
        "text": "Change the offer price to what they think is reasonable.",
        "rationale": "The agent cannot change the terms of the offer without the buyer's consent.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent has a duty to present all offers to the seller."
  },
  {
    "questionNumber": 125,
    "question": "A property is sold and the seller is a corporation. The deed must be signed by:",
    "answerOptions": [
      {
        "text": "The CEO of the corporation",
        "rationale": "The deed must be signed by an authorized officer of the corporation.",
        "isCorrect": false
      },
      {
        "text": "The board of directors",
        "rationale": "The board of directors authorizes the sale, but they do not all have to sign the deed.",
        "isCorrect": false
      },
      {
        "text": "An authorized officer of the corporation",
        "rationale": "The corporation's bylaws will specify who is authorized to sign documents on behalf of the corporation.",
        "isCorrect": true
      },
      {
        "text": "All of the shareholders",
        "rationale": "The shareholders do not have to sign the deed.",
        "isCorrect": false
      }
    ],
    "hint": "A corporation is a legal entity, and it must act through its authorized representatives."
  },
  {
    "questionNumber": 126,
    "question": "A buyer is purchasing a property and the seller has agreed to provide a home warranty. The buyer should:",
    "answerOptions": [
      {
        "text": "Read the home warranty policy to see what is covered.",
        "rationale": "The buyer should understand the terms and conditions of the home warranty before they rely on it.",
        "isCorrect": true
      },
      {
        "text": "Assume that everything is covered.",
        "rationale": "Home warranties have limitations and exclusions.",
        "isCorrect": false
      },
      {
        "text": "Cancel their homeowners insurance policy.",
        "rationale": "A home warranty is not a substitute for homeowners insurance.",
        "isCorrect": false
      },
      {
        "text": "Not have the property inspected.",
        "rationale": "A home warranty is not a substitute for a home inspection.",
        "isCorrect": false
      }
    ],
    "hint": "A home warranty is a service contract, and it is important to understand what it covers."
  },
  {
    "questionNumber": 127,
    "question": "A real estate agent is working with a seller who is an elderly person. The agent should:",
    "answerOptions": [
      {
        "text": "Take extra care to make sure that the seller understands all of the documents.",
        "rationale": "The agent has a duty to make sure that their client understands the transaction, and this is especially important when working with elderly clients.",
        "isCorrect": true
      },
      {
        "text": "Assume that the seller is not capable of making their own decisions.",
        "rationale": "The agent should not make assumptions about the seller's capacity.",
        "isCorrect": false
      },
      {
        "text": "Have the seller's children sign the documents.",
        "rationale": "The seller is the one who must sign the documents, unless they have given someone else a power of attorney.",
        "isCorrect": false
      },
      {
        "text": "Rush the seller through the transaction.",
        "rationale": "The agent should give the seller all the time they need to make a decision.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent has a duty to be patient and understanding with all of their clients."
  },
  {
    "questionNumber": 128,
    "question": "A property is sold and the seller agrees to finance a portion of the purchase price. This is known as:",
    "answerOptions": [
      {
        "text": "A conventional loan",
        "rationale": "A conventional loan is a loan from a traditional lender.",
        "isCorrect": false
      },
      {
        "text": "Seller financing",
        "rationale": "Seller financing is when the seller provides some or all of the financing for the purchase.",
        "isCorrect": true
      },
      {
        "text": "An FHA loan",
        "rationale": "An FHA loan is a loan that is insured by the Federal Housing Administration.",
        "isCorrect": false
      },
      {
        "text": "A VA loan",
        "rationale": "A VA loan is a loan that is guaranteed by the U.S. Department of Veterans Affairs.",
        "isCorrect": false
      }
    ],
    "hint": "This is a way for a buyer to purchase a property without having to get a loan from a traditional lender."
  },
  {
    "questionNumber": 129,
    "question": "A buyer is purchasing a property and the appraisal comes in below the sale price. The buyer can:",
    "answerOptions": [
      {
        "text": "Terminate the contract.",
        "rationale": "If the contract has an appraisal contingency, the buyer can terminate the contract if the appraisal comes in low.",
        "isCorrect": false
      },
      {
        "text": "Try to renegotiate the sale price with the seller.",
        "rationale": "The buyer can try to get the seller to lower the price to the appraised value.",
        "isCorrect": false
      },
      {
        "text": "Pay the difference between the appraised value and the sale price in cash.",
        "rationale": "The buyer can make up the difference in cash if they have the funds.",
        "isCorrect": false
      },
      {
        "text": "All of the above.",
        "rationale": "The buyer has several options in this situation, but terminating the contract is a primary right under the appraisal contingency.",
        "isCorrect": true
      }
    ],
    "hint": "A low appraisal can be a major obstacle in a real estate transaction, but there are ways to overcome it."
  },
  {
    "questionNumber": 130,
    "question": "A real estate agent is working with a buyer who is a friend. The agent should:",
    "answerOptions": [
      {
        "text": "Treat the friend like any other client.",
        "rationale": "The agent has the same fiduciary duties to a friend as they do to any other client.",
        "isCorrect": true
      },
      {
        "text": "Give the friend a discount on their commission.",
        "rationale": "The agent can choose to give the friend a discount, but they are not required to do so.",
        "isCorrect": false
      },
      {
        "text": "Not put anything in writing.",
        "rationale": "It is always a good idea to have a written agreement, especially when working with a friend.",
        "isCorrect": false
      },
      {
        "text": "Let the friend make all of the decisions.",
        "rationale": "The agent should still provide the friend with their professional advice.",
        "isCorrect": false
      }
    ],
    "hint": "Working with friends can be tricky, but it is important to maintain a professional relationship."
  },
  {
    "questionNumber": 131,
    "question": "A property is sold and the seller takes back a mortgage from the buyer. This is known as a:",
    "answerOptions": [
      {
        "text": "Purchase money mortgage",
        "rationale": "A purchase money mortgage is a mortgage that is given by the seller to the buyer to secure the payment of the purchase price.",
        "isCorrect": true
      },
      {
        "text": "Blanket mortgage",
        "rationale": "A blanket mortgage is a mortgage that covers more than one property.",
        "isCorrect": false
      },
      {
        "text": "Package mortgage",
        "rationale": "A package mortgage is a mortgage that includes both real and personal property.",
        "isCorrect": false
      },
      {
        "text": "Open-end mortgage",
        "rationale": "An open-end mortgage is a mortgage that allows the borrower to borrow additional funds up to a certain limit.",
        "isCorrect": false
      }
    ],
    "hint": "This is a type of seller financing."
  },
  {
    "questionNumber": 132,
    "question": "A buyer is purchasing a property and wants to make sure that they have clear title. The buyer should:",
    "answerOptions": [
      {
        "text": "Get a title insurance policy.",
        "rationale": "A title insurance policy will protect the buyer from financial loss due to defects in the title.",
        "isCorrect": true
      },
      {
        "text": "Do a title search themselves.",
        "rationale": "A title search should be done by a professional.",
        "isCorrect": false
      },
      {
        "text": "Ask the seller if the title is clear.",
        "rationale": "The buyer should not rely solely on the seller's representations.",
        "isCorrect": false
      },
      {
        "text": "Assume that the title is clear.",
        "rationale": "The buyer should not make assumptions about the title.",
        "isCorrect": false
      }
    ],
    "hint": "Clear title is essential to a real estate transaction, and there are steps that a buyer can take to protect themselves."
  },
  {
    "questionNumber": 133,
    "question": "A real estate agent is working with a seller who wants to overprice their property. The agent should:",
    "answerOptions": [
      {
        "text": "Take the listing at the seller's price.",
        "rationale": "The agent can take the listing, but they should also advise the seller of the potential consequences of overpricing the property.",
        "isCorrect": false
      },
      {
        "text": "Refuse to take the listing.",
        "rationale": "The agent can refuse to take the listing if they believe that the property is overpriced to the point where it will not sell.",
        "isCorrect": false
      },
      {
        "text": "Provide the seller with a CMA and explain the importance of pricing the property correctly.",
        "rationale": "The agent should educate the seller about the market and help them come to a realistic listing price.",
        "isCorrect": true
      },
      {
        "text": "Lie to the seller and tell them that their price is reasonable.",
        "rationale": "This would be a breach of the agent's fiduciary duty to the client.",
        "isCorrect": false
      }
    ],
    "hint": "Pricing a property correctly is one of the most important things that a real estate agent does."
  },
  {
    "questionNumber": 134,
    "question": "A property is sold and the buyer and seller agree to split the closing costs. This should be stated in the:",
    "answerOptions": [
      {
        "text": "Deed",
        "rationale": "The deed transfers title to the real property.",
        "isCorrect": false
      },
      {
        "text": "Mortgage",
        "rationale": "The mortgage is the loan agreement.",
        "isCorrect": false
      },
      {
        "text": "Purchase and sale agreement",
        "rationale": "The purchase and sale agreement should specify how the closing costs will be divided between the buyer and the seller.",
        "isCorrect": true
      },
      {
        "text": "Title insurance policy",
        "rationale": "The title insurance policy protects the buyer's title to the real property.",
        "isCorrect": false
      }
    ],
    "hint": "The purchase and sale agreement is the main contract that governs the transaction, and it should include all of the details."
  },
  {
    "questionNumber": 135,
    "question": "A buyer is purchasing a property and the seller has agreed to leave the refrigerator.  At the final walk-through, the buyer discovers that the seller has taken the refrigerator. The buyer should:",
    "answerOptions": [
      {
        "text": "Refuse to close.",
        "rationale": "The buyer can refuse to close until the seller returns the refrigerator or compensates them for it.",
        "isCorrect": true
      },
      {
        "text": "Close and then sue the seller.",
        "rationale": "While the buyer may have legal recourse, it is better to resolve the issue before closing.",
        "isCorrect": false
      },
      {
        "text": "Buy a new refrigerator.",
        "rationale": "The buyer should not have to buy a new refrigerator if the seller agreed to leave the old one.",
        "isCorrect": false
      },
      {
        "text": "Do nothing.",
        "rationale": "The buyer should take action to protect their interests.",
        "isCorrect": false
      }
    ],
    "hint": "The final walk-through is the buyer's last chance to make sure that the property is in the agreed-upon condition."
  },
  {
    "questionNumber": 136,
    "question": "A real estate agent is working with a buyer who is very demanding. The agent should:",
    "answerOptions": [
      {
        "text": "Be patient and professional.",
        "rationale": "The agent should always maintain a professional demeanor, even with difficult clients.",
        "isCorrect": true
      },
      {
        "text": "Tell the buyer to find another agent.",
        "rationale": "The agent can choose to terminate the agency relationship, but they should do so in a professional manner.",
        "isCorrect": false
      },
      {
        "text": "Ignore the buyer's requests.",
        "rationale": "This would be a breach of the agent's fiduciary duty to the client.",
        "isCorrect": false
      },
      {
        "text": "Complain about the buyer to other agents.",
        "rationale": "This would be unprofessional.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent must be able to work with all types of people."
  },
  {
    "questionNumber": 137,
    "question": "A property is sold and the seller is a partnership. The deed must be signed by:",
    "answerOptions": [
      {
        "text": "All of the partners",
        "rationale": "The partnership agreement will specify who is authorized to sign documents on behalf of the partnership.",
        "isCorrect": false
      },
      {
        "text": "The managing partner",
        "rationale": "This may be the case, but it depends on the partnership agreement.",
        "isCorrect": false
      },
      {
        "text": "An authorized partner",
        "rationale": "The partnership agreement will specify who is authorized to sign documents on behalf of the partnership.",
        "isCorrect": true
      },
      {
        "text": "A majority of the partners",
        "rationale": "This may be the case, but it depends on the partnership agreement.",
        "isCorrect": false
      }
    ],
    "hint": "A partnership is a legal entity, and it must act through its authorized representatives."
  },
  {
    "questionNumber": 138,
    "question": "A buyer is purchasing a property and wants to have it tested for radon. The buyer should:",
    "answerOptions": [
      {
        "text": "Hire a certified radon tester.",
        "rationale": "Radon testing should be done by a professional.",
        "isCorrect": true
      },
      {
        "text": "Buy a do-it-yourself radon test kit.",
        "rationale": "While do-it-yourself kits are available, they are not as accurate as a professional test.",
        "isCorrect": false
      },
      {
        "text": "Ask the seller if the property has radon.",
        "rationale": "The seller may not know if the property has radon, and even if they do, the buyer should still have the property tested.",
        "isCorrect": false
      },
      {
        "text": "Assume that the property does not have radon.",
        "rationale": "The buyer should not make assumptions about radon.",
        "isCorrect": false
      }
    ],
    "hint": "Radon is a serious environmental hazard, and it is important to have the property tested."
  },
  {
    "questionNumber": 139,
    "question": "A real estate agent is working with a seller who is going through a divorce. The agent should:",
    "answerOptions": [
      {
        "text": "Get both spouses to sign the listing agreement.",
        "rationale": "If both spouses are on the title to the property, they both need to sign the listing agreement.",
        "isCorrect": true
      },
      {
        "text": "Only get one spouse to sign the listing agreement.",
        "rationale": "This could lead to legal problems.",
        "isCorrect": false
      },
      {
        "text": "Give the spouses legal advice.",
        "rationale": "A real estate agent is not a lawyer and should not give legal advice.",
        "isCorrect": false
      },
      {
        "text": "Take sides in the divorce.",
        "rationale": "The agent should remain neutral.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent must be careful when working with clients who are going through a divorce."
  },
  {
    "questionNumber": 140,
    "question": "A property is sold and the seller is an estate. The deed must be signed by the:",
    "answerOptions": [
      {
        "text": "Heirs of the deceased",
        "rationale": "The executor or administrator of the estate is the one who signs the deed.",
        "isCorrect": false
      },
      {
        "text": "Executor or administrator of the estate",
        "rationale": "The executor (if there is a will) or the administrator (if there is no will) is the person who is authorized to act on behalf of the estate.",
        "isCorrect": true
      },
      {
        "text": "Deceased person",
        "rationale": "The deceased person cannot sign the deed.",
        "isCorrect": false
      },
      {
        "text": "Judge of the probate court",
        "rationale": "The judge of the probate court oversees the administration of the estate, but they do not sign the deed.",
        "isCorrect": false
      }
    ],
    "hint": "An estate is a legal entity, and it must act through its authorized representative."
  },
  {
    "questionNumber": 141,
    "question": "A buyer is purchasing a property and the seller has agreed to pay for a title insurance policy. The buyer should:",
    "answerOptions": [
      {
        "text": "Also get their own title insurance policy.",
        "rationale": "The seller is paying for the lender's title insurance policy, which protects the lender. The buyer should also get an owner's title insurance policy to protect themselves.",
        "isCorrect": true
      },
      {
        "text": "Not get a title insurance policy.",
        "rationale": "The buyer should always get an owner's title insurance policy.",
        "isCorrect": false
      },
      {
        "text": "Rely on the seller's title insurance policy.",
        "rationale": "The seller is paying for the lender's title insurance policy, not the owner's title insurance policy.",
        "isCorrect": false
      },
      {
        "text": "Cancel the contract.",
        "rationale": "This would not be appropriate.",
        "isCorrect": false
      }
    ],
    "hint": "There are two types of title insurance policies, and it is important to know the difference."
  },
  {
    "questionNumber": 142,
    "question": "A real estate agent is working with a buyer who is a first-time homebuyer. The agent should:",
    "answerOptions": [
      {
        "text": "Explain the home buying process to the buyer in detail.",
        "rationale": "First-time homebuyers often need extra guidance, and the agent should be patient and thorough in their explanations.",
        "isCorrect": true
      },
      {
        "text": "Assume that the buyer knows what they are doing.",
        "rationale": "The agent should not make assumptions about the buyer's knowledge.",
        "isCorrect": false
      },
      {
        "text": "Rush the buyer through the transaction.",
        "rationale": "The agent should give the buyer all the time they need to make a decision.",
        "isCorrect": false
      },
      {
        "text": "Tell the buyer to do their own research.",
        "rationale": "While the buyer should do their own research, the agent is still responsible for guiding them through the process.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent has a duty to be patient and understanding with all of their clients."
  },
  {
    "questionNumber": 143,
    "question": "A property is sold and the seller is a limited liability company (LLC). The deed must be signed by:",
    "answerOptions": [
      {
        "text": "All of the members of the LLC",
        "rationale": "The operating agreement of the LLC will specify who is authorized to sign documents on behalf of the LLC.",
        "isCorrect": false
      },
      {
        "text": "The managing member of the LLC",
        "rationale": "This may be the case, but it depends on the operating agreement.",
        "isCorrect": false
      },
      {
        "text": "An authorized member or manager of the LLC",
        "rationale": "The operating agreement of the LLC will specify who is authorized to sign documents on behalf of the LLC.",
        "isCorrect": true
      },
      {
        "text": "A majority of the members of the LLC",
        "rationale": "This may be the case, but it depends on the operating agreement.",
        "isCorrect": false
      }
    ],
    "hint": "An LLC is a legal entity, and it must act through its authorized representatives."
  },
  {
    "questionNumber": 144,
    "question": "A buyer is purchasing a property and the seller has agreed to pay for a survey. The buyer should:",
    "answerOptions": [
      {
        "text": "Review the survey to make sure that there are no encroachments or boundary line issues.",
        "rationale": "The buyer should carefully review the survey and ask questions if they have any concerns.",
        "isCorrect": true
      },
      {
        "text": "Not look at the survey.",
        "rationale": "The survey contains important information about the property.",
        "isCorrect": false
      },
      {
        "text": "Assume that the survey is accurate.",
        "rationale": "The buyer should review the survey to make sure that it is accurate.",
        "isCorrect": false
      },
      {
        "text": "Cancel the contract if they do not like the survey.",
        "rationale": "This would not be appropriate unless the survey reveals a major problem with the property.",
        "isCorrect": false
      }
    ],
    "hint": "A survey is an important document, and the buyer should take the time to review it carefully."
  },
  {
    "questionNumber": 145,
    "question": "A real estate agent is working with a seller who is a difficult person. The agent should:",
    "answerOptions": [
      {
        "text": "Be patient and professional.",
        "rationale": "The agent should always maintain a professional demeanor, even with difficult clients.",
        "isCorrect": true
      },
      {
        "text": "Tell the seller to find another agent.",
        "rationale": "The agent can choose to terminate the agency relationship, but they should do so in a professional manner.",
        "isCorrect": false
      },
      {
        "text": "Ignore the seller's requests.",
        "rationale": "This would be a breach of the agent's fiduciary duty to the client.",
        "isCorrect": false
      },
      {
        "text": "Complain about the seller to other agents.",
        "rationale": "This would be unprofessional.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent must be able to work with all types of people."
  },
  {
    "questionNumber": 146,
    "question": "A property is sold and the seller is a trust. The deed must be signed by the:",
    "answerOptions": [
      {
        "text": "Trustor",
        "rationale": "The trustor is the person who creates the trust.",
        "isCorrect": false
      },
      {
        "text": "Trustee",
        "rationale": "The trustee is the person who holds legal title to the property and is responsible for managing the trust.",
        "isCorrect": true
      },
      {
        "text": "Beneficiary",
        "rationale": "The beneficiary is the person who benefits from the trust.",
        "isCorrect": false
      },
      {
        "text": "Grantor",
        "rationale": "The grantor is another name for the trustor.",
        "isCorrect": false
      }
    ],
    "hint": "A trust is a legal entity, and it must act through its authorized representative."
  },
  {
    "questionNumber": 147,
    "question": "A buyer is purchasing a property and the seller has agreed to pay for a home inspection. The buyer should:",
    "answerOptions": [
      {
        "text": "Still hire their own home inspector.",
        "rationale": "The buyer should always hire their own home inspector to represent their interests.",
        "isCorrect": true
      },
      {
        "text": "Not get a home inspection.",
        "rationale": "The buyer should always get a home inspection.",
        "isCorrect": false
      },
      {
        "text": "Rely on the seller's home inspection.",
        "rationale": "The seller's home inspector may not be as thorough as the buyer's home inspector.",
        "isCorrect": false
      },
      {
        "text": "Cancel the contract.",
        "rationale": "This would not be appropriate.",
        "isCorrect": false
      }
    ],
    "hint": "A home inspection is a critical part of the home buying process, and the buyer should have their own inspector."
  },
  {
    "questionNumber": 148,
    "question": "A real estate agent is working with a buyer who is a real estate investor. The agent should:",
    "answerOptions": [
      {
        "text": "Provide the investor with information about potential investment properties.",
        "rationale": "The agent should be a resource for the investor and provide them with the information they need to make a good investment.",
        "isCorrect": true
      },
      {
        "text": "Give the investor financial advice.",
        "rationale": "A real estate agent is not a financial advisor and should not give financial advice.",
        "isCorrect": false
      },
      {
        "text": "Tell the investor which properties to buy.",
        "rationale": "The agent can provide information, but the investor must make their own decisions.",
        "isCorrect": false
      },
      {
        "text": "Guarantee that the investor will make a profit.",
        "rationale": "The agent cannot guarantee that the investor will make a profit.",
        "isCorrect": false
      }
    ],
    "hint": "A real estate agent can be a valuable resource for a real estate investor."
  },
  {
    "questionNumber": 149,
    "question": "A property is sold and the seller is a minor. The contract is:",
    "answerOptions": [
      {
        "text": "Void",
        "rationale": "A contract with a minor is not automatically void.",
        "isCorrect": false
      },
      {
        "text": "Voidable by the minor.",
        "rationale": "A contract entered into by a minor is voidable at the option of the minor.",
        "isCorrect": true
      },
      {
        "text": "Voidable by the adult party.",
        "rationale": "Only the minor has the option to void the contract.",
        "isCorrect": false
      },
      {
        "text": "Valid and enforceable.",
        "rationale": "The minor has the right to disaffirm the contract.",
        "isCorrect": false
      }
    ],
    "hint": "The law gives special protection to minors who enter into contracts."
  },
  {
    "questionNumber": 150,
    "question": "A buyer is purchasing a property and the seller has agreed to pay for a radon test. The buyer should:",
    "answerOptions": [
      {
        "text": "Review the results of the radon test.",
        "rationale": "The buyer should carefully review the results of the radon test and ask questions if they have any concerns.",
        "isCorrect": true
      },
      {
        "text": "Not look at the results of the radon test.",
        "rationale": "The results of the radon test contain important information about the property.",
        "isCorrect": false
      },
      {
        "text": "Assume that the results of the radon test are acceptable.",
        "rationale": "The buyer should review the results of the radon test to make sure that they are acceptable.",
        "isCorrect": false
      },
      {
        "text": "Cancel the contract if they do not like the results of the radon test.",
        "rationale": "This would not be appropriate unless the results of the radon test reveal a major problem with the property.",
        "isCorrect": false
      }
    ],
    "hint": "A radon test is an important part of the home buying process, and the buyer should take the time to review the results carefully."
  },
  {
    "questionNumber": 151,
    "question": "A real estate agent is working with a seller who is a family member. The agent should:",
    "answerOptions": [
      {
        "text": "Treat the family member like any other client.",
        "rationale": "The agent has the same fiduciary duties to a family member as they do to any other client.",
        "isCorrect": true
      },
      {
        "text": "Give the family member a discount on their commission.",
        "rationale": "The agent can choose to give the family member a discount, but they are not required to do so.",
        "isCorrect": false
      },
      {
        "text": "Not put anything in writing.",
        "rationale": "It is always a good idea to have a written agreement, especially when working with a family member.",
        "isCorrect": false
      },
      {
        "text": "Let the family member make all of the decisions.",
        "rationale": "The agent should still provide the family member with their professional advice.",
        "isCorrect": false
      }
    ],
    "hint": "Working with family members can be tricky, but it is important to maintain a professional relationship."
  },
  {
    "questionNumber": 152,
    "question": "A property is sold and the seller is a power of attorney. The deed must be signed by the:",
    "answerOptions": [
      {
        "text": "Principal",
        "rationale": "The principal is the person who gave the power of attorney.",
        "isCorrect": false
      },
      {
        "text": "Agent",
        "rationale": "The agent, also known as the attorney-in-fact, is the person who is authorized to act on behalf of the principal.",
        "isCorrect": true
      },
      {
        "text": "Notary public",
        "rationale": "The notary public witnesses the signature, but they do not sign the deed.",
        "isCorrect": false
      },
      {
        "text": "Closing attorney",
        "rationale": "The closing attorney prepares the deed, but they do not sign it on behalf of the seller.",
        "isCorrect": false
      }
    ],
    "hint": "A power of attorney is a legal document that gives one person the authority to act on behalf of another."
  }
];
